/**
 * Elite Batch 18 Part 4 — Workflow Templates 1708–1727
 * AI first-line generation, AI message sequence drafter, AWS SES bulk email send,
 * account build from website URL, account contact gap analysis, ads intelligence
 * personalization, affiliate partner recruitment, affiliate recruiting pipeline,
 * agency referral partner recruiting, alumni affinity outreach, blocked domain
 * suppression, blog reader nurture to SDR handoff, board advisor introduction,
 * bounce handling re-enrichment, bounce maintenance cleanup, broken link reclaim
 * outreach, CRM API retry handler, CRM ownership reassignment, CRM-powered
 * discovery call prep, and CSV cleanup deliverability check workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 18 PART 4 — TEMPLATES 1708–1727
// =============================================================================

export const ELITE_BATCH18_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1708. AI FIRST-LINE GENERATION (7 nodes)
  // ===================================================================

  "AI First-Line Generation": {
    summary:
      "Fires when outreach prep is requested via webhook, extracts prospect data with AI, researches the company via Clearbit API, uses AI to analyze company context, generates a personalized first-line with AI, scores quality with runCode, integrates into the outreach sequence via API, and tracks performance in Sheets.",
    nodes: [
      trigger("Webhook: Outreach prep requested", "webhook_app", {
        source: "sales_automation",
        event: "outreach.prep_requested",
        howItStarts:
          "Fires when a rep triggers outreach prep for a prospect — including target email, company domain, and any known context — to auto-generate a hyper-personalized opening line for cold emails",
        setup:
          "Configure sales automation webhook for outreach prep events. Set OpenAI API key for AI analysis and first-line generation. Provide Clearbit API key for company research. Configure outreach sequence API credentials. Set Google Sheets credentials for performance tracking.",
        inputFields: [],
      }),

      // 2. Prospect research via Clearbit
      n("2", "hitApi", "Research Prospect via Clearbit", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={prospect_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "company_name", type: "string" },
            {
              name: "description",
              customName: "company_description",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "category.subIndustry",
              customName: "company_sub_industry",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "company_employees",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            { name: "tech", customName: "company_tech_stack", type: "string" },
            { name: "site.url", customName: "company_website", type: "string" },
          ],
        },
      }),

      // 3. AI company context analysis
      aiNode(
        "3",
        "AI Analyze Company Context",
        520,
        "You are an elite sales research analyst. Given raw company data from Clearbit and a prospect domain, analyze the company context to identify key talking points: recent news, growth signals, technology choices, competitive landscape, and pain points typical for their industry and size. Respond JSON: {key_insight: string, growth_signal: string, tech_angle: string, pain_point: string, competitive_context: string, recent_trigger: string, personalization_hook: string}",
        "Company: {company_name}. Description: {company_description}. Industry: {company_industry} / {company_sub_industry}. Employees: {company_employees}. Revenue: {company_revenue}. Tech stack: {company_tech_stack}. Website: {company_website}. Domain: {prospect_domain}. Analyze for outreach personalization hooks.",
        [
          { field: "key_insight", as: "key_insight", type: "string" },
          { field: "growth_signal", as: "growth_signal", type: "string" },
          { field: "tech_angle", as: "tech_angle", type: "string" },
          { field: "pain_point", as: "pain_point", type: "string" },
          {
            field: "competitive_context",
            as: "competitive_context",
            type: "string",
          },
          { field: "recent_trigger", as: "recent_trigger", type: "string" },
          {
            field: "personalization_hook",
            as: "personalization_hook",
            type: "string",
          },
        ],
      ),

      // 4. AI first-line generator
      aiNode(
        "4",
        "AI Generate Personalized First-Line",
        760,
        "You are a world-class cold email copywriter. Using the company context, personalization hooks, and prospect data, write 3 hyper-personalized opening lines for a cold email. Each must reference something specific about the prospect's company — not generic flattery. Lines should be 15-25 words, conversational, and create genuine curiosity. Respond JSON: {first_line_1: string, first_line_2: string, first_line_3: string, best_pick: string, reasoning: string, tone: string}",
        "Company: {company_name} ({company_industry}). Key insight: {key_insight}. Growth signal: {growth_signal}. Tech angle: {tech_angle}. Pain point: {pain_point}. Hook: {personalization_hook}. Trigger: {recent_trigger}. Prospect domain: {prospect_domain}. Generate 3 first-lines ranked by specificity.",
        [
          { field: "first_line_1", as: "first_line_1", type: "string" },
          { field: "first_line_2", as: "first_line_2", type: "string" },
          { field: "first_line_3", as: "first_line_3", type: "string" },
          { field: "best_pick", as: "best_first_line", type: "string" },
          { field: "reasoning", as: "line_reasoning", type: "string" },
          { field: "tone", as: "line_tone", type: "string" },
        ],
      ),

      // 5. Quality scoring runCode
      n("5", "runCode", "Score First-Line Quality", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score the quality of generated first-lines
const line = vars.best_first_line || "";
const words = line.split(/\\s+/).length;
let quality_score = 70;
// Length check: ideal 15-25 words
if (words >= 15 && words <= 25) quality_score += 10;
else if (words < 10 || words > 35) quality_score -= 15;
// Check for company name mention (specificity)
const companyName = (vars.company_name || "").toLowerCase();
if (line.toLowerCase().includes(companyName)) quality_score += 10;
// Check for generic phrases to penalize
const genericPhrases = ["i noticed", "i saw that", "congrats on", "impressive", "love what you"];
const hasGeneric = genericPhrases.some(p => line.toLowerCase().includes(p));
if (hasGeneric) quality_score -= 10;
// Check for question format (higher engagement)
if (line.includes("?")) quality_score += 5;
// Ensure no placeholder tokens remain
if (line.includes("{") || line.includes("}")) quality_score -= 30;
const finalScore = Math.max(0, Math.min(100, quality_score));
const is_approved = finalScore >= 60 ? "true" : "false";
return { quality_score: finalScore, is_approved: is_approved, word_count: words, has_specificity: line.toLowerCase().includes(companyName) ? "true" : "false", quality_notes: hasGeneric ? "contains_generic_phrase" : "passes_quality_check" };`,
          outputVariables: [
            { name: "quality_score", type: "number" },
            { name: "is_approved", type: "string" },
            { name: "word_count", type: "number" },
            { name: "has_specificity", type: "string" },
            { name: "quality_notes", type: "string" },
          ],
        },
      }),

      // 6. Sequence integration API
      n("6", "hitApi", "Push First-Line to Outreach Sequence", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.outreach.io/api/v2/sequences/{sequence_id}/steps",
          headers: [
            { key: "Authorization", value: "Bearer {outreach_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"type":"sequenceStep","attributes":{"personalizedFirstLine":"{best_first_line}","prospectDomain":"{prospect_domain}","qualityScore":"{quality_score}","tone":"{line_tone}"}}',
            },
          ],
          variables: [
            { name: "data.id", customName: "sequence_step_id", type: "string" },
            {
              name: "data.attributes.status",
              customName: "step_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Performance tracking Sheets
      sheetsNode(
        "7",
        "Log First-Line Performance to Sheets",
        1480,
        "FirstLineTracker",
        '[["{prospect_domain}","{company_name}","{company_industry}","{best_first_line}","{quality_score}","{has_specificity}","{line_tone}","{quality_notes}","{sequence_step_id}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1709. AI MESSAGE SEQUENCE DRAFTER (8 nodes)
  // ===================================================================

  "AI Message Sequence Drafter": {
    summary:
      "Fires when a sequence request arrives via webhook, extracts lead data from HubSpot, builds the ICP messaging framework with runCode, drafts a 3-step sequence (intro, value-add, direct CTA) with AI nodes, gates through rep approval, and activates the sequence via API.",
    nodes: [
      trigger("Webhook: Sequence draft requested", "webhook_app", {
        source: "sales_ops",
        event: "sequence.draft_requested",
        howItStarts:
          "Fires when a rep or SDR requests an AI-drafted outreach sequence for a specific lead — including lead ID, target persona, and campaign context — to auto-generate a complete 3-step email sequence",
        setup:
          "Configure sales ops webhook for sequence draft requests. Set HubSpot token for lead data retrieval. Set OpenAI API key for sequence drafting. Configure outreach platform API for sequence activation.",
        inputFields: [],
      }),

      // 2. Lead data retrieval HubSpot
      hubspotGetNode(
        "2",
        "Retrieve Lead Data from HubSpot",
        280,
        "crm/v3/objects/contacts/{lead_id}?properties=email,firstname,lastname,company,jobtitle,industry,hs_lead_status,notes_last_contacted,lifecyclestage",
        [
          {
            name: "properties.email",
            customName: "lead_email",
            type: "string",
          },
          {
            name: "properties.firstname",
            customName: "lead_first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "lead_last_name",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "lead_company",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "lead_title",
            type: "string",
          },
          {
            name: "properties.industry",
            customName: "lead_industry",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
        ],
      ),

      // 3. ICP messaging framework lookup runCode
      n("3", "runCode", "Build ICP Messaging Framework", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build messaging framework based on ICP persona and industry
const title = (vars.lead_title || "").toLowerCase();
const industry = (vars.lead_industry || "").toLowerCase();
let persona = "general";
let pain_points = "";
let value_props = "";
let cta_style = "soft";
if (title.includes("cto") || title.includes("vp engineering") || title.includes("head of engineering")) {
  persona = "technical_leader";
  pain_points = "engineering velocity, technical debt, developer experience, system reliability";
  value_props = "faster ship cycles, reduced incidents, developer productivity gains";
  cta_style = "technical_demo";
} else if (title.includes("cfo") || title.includes("vp finance") || title.includes("finance")) {
  persona = "finance_leader";
  pain_points = "cost optimization, ROI visibility, budget forecasting, vendor consolidation";
  value_props = "measurable cost savings, ROI within 90 days, transparent pricing";
  cta_style = "roi_calculator";
} else if (title.includes("ceo") || title.includes("founder") || title.includes("president")) {
  persona = "executive";
  pain_points = "revenue growth, competitive edge, operational efficiency, market expansion";
  value_props = "strategic advantage, revenue acceleration, market positioning";
  cta_style = "executive_briefing";
} else if (title.includes("marketing") || title.includes("cmo") || title.includes("growth")) {
  persona = "marketing_leader";
  pain_points = "pipeline generation, conversion rates, attribution, campaign performance";
  value_props = "pipeline acceleration, better attribution, higher conversion rates";
  cta_style = "case_study_review";
} else {
  persona = "ops_manager";
  pain_points = "workflow efficiency, manual processes, team productivity, data silos";
  value_props = "automation savings, workflow streamlining, cross-team visibility";
  cta_style = "product_walkthrough";
}
const tone = persona === "executive" ? "concise_strategic" : persona === "technical_leader" ? "technical_credible" : "consultative_helpful";
return { persona: persona, pain_points: pain_points, value_props: value_props, cta_style: cta_style, messaging_tone: tone, sequence_length: "3" };`,
          outputVariables: [
            { name: "persona", type: "string" },
            { name: "pain_points", type: "string" },
            { name: "value_props", type: "string" },
            { name: "cta_style", type: "string" },
            { name: "messaging_tone", type: "string" },
            { name: "sequence_length", type: "string" },
          ],
        },
      }),

      // 4. AI step 1 draft introduction
      aiNode(
        "4",
        "AI Draft Step 1: Introduction Email",
        760,
        "You are an elite SDR email copywriter. Write Step 1 of a 3-step outreach sequence: the introduction email. This must be under 100 words, lead with a personalized observation about the prospect's company, reference a relevant pain point for their persona, and end with a soft question (not a hard CTA). No fluff, no 'I hope this finds you well'. Respond JSON: {step1_subject: string, step1_body: string, step1_word_count: number}",
        "Lead: {lead_first_name} {lead_last_name}, Title: {lead_title}, Company: {lead_company}, Industry: {lead_industry}. Persona: {persona}. Pain points: {pain_points}. Value props: {value_props}. Tone: {messaging_tone}. Write the Step 1 introduction email.",
        [
          { field: "step1_subject", as: "step1_subject", type: "string" },
          { field: "step1_body", as: "step1_body", type: "string" },
          { field: "step1_word_count", as: "step1_word_count", type: "number" },
        ],
      ),

      // 5. AI step 2 draft value add
      aiNode(
        "5",
        "AI Draft Step 2: Value-Add Email",
        1000,
        "You are an elite SDR email copywriter. Write Step 2 of a 3-step outreach sequence: the value-add follow-up. This arrives 3 days after Step 1. Reference the first email indirectly, share a relevant insight, stat, or mini case study related to their pain points. Keep under 80 words. No guilt-tripping about not responding. Respond JSON: {step2_subject: string, step2_body: string, step2_word_count: number}",
        "Lead: {lead_first_name}, Company: {lead_company}, Industry: {lead_industry}. Persona: {persona}. Pain points: {pain_points}. Value props: {value_props}. Tone: {messaging_tone}. Step 1 subject was: {step1_subject}. Write the Step 2 value-add email.",
        [
          { field: "step2_subject", as: "step2_subject", type: "string" },
          { field: "step2_body", as: "step2_body", type: "string" },
          { field: "step2_word_count", as: "step2_word_count", type: "number" },
        ],
      ),

      // 6. AI step 3 draft direct CTA
      aiNode(
        "6",
        "AI Draft Step 3: Direct CTA Email",
        1240,
        "You are an elite SDR email copywriter. Write Step 3 of a 3-step outreach sequence: the direct ask. This arrives 5 days after Step 2. Be direct but respectful. Include a specific CTA matching the persona type. Keep under 60 words. Make it easy to say yes or no. Respond JSON: {step3_subject: string, step3_body: string, step3_word_count: number, cta_text: string}",
        "Lead: {lead_first_name}, Company: {lead_company}. Persona: {persona}. CTA style: {cta_style}. Tone: {messaging_tone}. Previous subjects: {step1_subject}, {step2_subject}. Write the Step 3 direct CTA email.",
        [
          { field: "step3_subject", as: "step3_subject", type: "string" },
          { field: "step3_body", as: "step3_body", type: "string" },
          { field: "step3_word_count", as: "step3_word_count", type: "number" },
          { field: "cta_text", as: "cta_text", type: "string" },
        ],
      ),

      // 7. Rep review approval gate
      n("7", "approvalGate", "Rep Review and Approval Gate", 1480, {
        approvalGateDetails: {
          approverEmail: "{rep_email}",
          approvalMessage:
            "AI-Drafted 3-Step Sequence Ready for Review\\n\\nLead: {lead_first_name} {lead_last_name} ({lead_title}) at {lead_company}\\nPersona: {persona} | Tone: {messaging_tone}\\n\\nStep 1 — {step1_subject}:\\n{step1_body}\\n\\nStep 2 — {step2_subject}:\\n{step2_body}\\n\\nStep 3 — {step3_subject}:\\n{step3_body}\\n\\nApprove to activate the sequence, or reject to regenerate.",
        },
      }),

      // 8. Sequence activation API
      n("8", "hitApi", "Activate Sequence in Outreach Platform", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.outreach.io/api/v2/sequences",
          headers: [
            { key: "Authorization", value: "Bearer {outreach_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"type":"sequence","attributes":{"name":"AI Sequence for {lead_first_name} {lead_last_name}","steps":[{"subject":"{step1_subject}","body":"{step1_body}","delayDays":0},{"subject":"{step2_subject}","body":"{step2_body}","delayDays":3},{"subject":"{step3_subject}","body":"{step3_body}","delayDays":5}],"prospectEmail":"{lead_email}"}}',
            },
          ],
          variables: [
            { name: "data.id", customName: "sequence_id", type: "string" },
            {
              name: "data.attributes.state",
              customName: "sequence_state",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1710. AWS SES BULK EMAIL SEND (8 nodes)
  // ===================================================================

  "AWS SES Bulk Email Send": {
    summary:
      "Launches a bulk email campaign with manual trigger, validates the recipient list via API, segments with AI personalization, builds a rate-limited send queue with runCode, executes tracked sends via SES API, routes bounces and complaints through a condition, writes back to CRM via HubSpot, and alerts campaign analytics on Slack.",
    nodes: [
      trigger("Manual: Launch bulk email campaign", "manual", {
        source: "campaign_manager",
        event: "campaign.launch",
        howItStarts:
          "Manually triggered by the campaign manager to launch a bulk email send via AWS SES — including campaign ID, template ID, recipient list reference, and send parameters",
        setup:
          "Configure AWS SES credentials and sending domain. Set OpenAI API key for AI segmentation. Provide recipient list API endpoint. Set HubSpot token for CRM writeback. Configure Slack webhook for analytics alerts.",
        inputFields: [
          {
            name: "campaign_id",
            type: "string",
            example: "camp_2024_q1_product_launch",
          },
          {
            name: "template_id",
            type: "string",
            example: "tmpl_product_launch_v2",
          },
          {
            name: "list_id",
            type: "string",
            example: "list_enterprise_prospects",
          },
          {
            name: "from_email",
            type: "string",
            example: "campaigns@company.com",
          },
          { name: "send_rate_per_second", type: "string", example: "14" },
        ],
      }),

      // 2. Recipient list validation API
      n("2", "hitApi", "Validate Recipient List via API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-platform.com/v1/lists/{list_id}/validate",
          headers: [
            { key: "Authorization", value: "Bearer {platform_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "total_recipients",
              customName: "total_recipients",
              type: "number",
            },
            {
              name: "valid_count",
              customName: "valid_recipients",
              type: "number",
            },
            {
              name: "invalid_count",
              customName: "invalid_recipients",
              type: "number",
            },
            {
              name: "suppressed_count",
              customName: "suppressed_count",
              type: "number",
            },
            {
              name: "list_health_score",
              customName: "list_health_score",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI list segmentation personalization
      aiNode(
        "3",
        "AI Segment and Personalize List",
        520,
        "You are an email campaign strategist. Given the validated recipient list stats, campaign template, and sending parameters, create personalization segments. Group recipients by industry, company size, and engagement history. Assign subject line variants and send-time optimization windows. Respond JSON: {segments: string, segment_count: number, primary_subject: string, variant_a_subject: string, variant_b_subject: string, optimal_send_window: string, personalization_tokens: string, estimated_open_rate: string}",
        "Campaign: {campaign_id}. Template: {template_id}. Total valid recipients: {valid_recipients}. Suppressed: {suppressed_count}. List health: {list_health_score}%. From: {from_email}. Segment and personalize this campaign send.",
        [
          { field: "segments", as: "segments", type: "string" },
          { field: "segment_count", as: "segment_count", type: "number" },
          { field: "primary_subject", as: "primary_subject", type: "string" },
          {
            field: "variant_a_subject",
            as: "variant_a_subject",
            type: "string",
          },
          {
            field: "variant_b_subject",
            as: "variant_b_subject",
            type: "string",
          },
          {
            field: "optimal_send_window",
            as: "optimal_send_window",
            type: "string",
          },
          {
            field: "personalization_tokens",
            as: "personalization_tokens",
            type: "string",
          },
          {
            field: "estimated_open_rate",
            as: "estimated_open_rate",
            type: "string",
          },
        ],
      ),

      // 4. Rate-limited send queue runCode
      n("4", "runCode", "Build Rate-Limited Send Queue", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build rate-limited send queue for AWS SES
const totalRecipients = parseInt(vars.valid_recipients) || 0;
const ratePerSecond = parseInt(vars.send_rate_per_second) || 14;
const segmentCount = parseInt(vars.segment_count) || 1;
const batchSize = Math.min(50, ratePerSecond);
const totalBatches = Math.ceil(totalRecipients / batchSize);
const estimatedDurationSeconds = Math.ceil(totalRecipients / ratePerSecond);
const estimatedDurationMinutes = Math.ceil(estimatedDurationSeconds / 60);
const sendWindows = [];
for (let i = 0; i < totalBatches; i++) {
  sendWindows.push({ batch: i + 1, offset: i * batchSize, limit: batchSize, delay_ms: i * Math.ceil(1000 / ratePerSecond) * batchSize });
}
return {
  total_batches: totalBatches,
  batch_size: batchSize,
  estimated_duration_minutes: estimatedDurationMinutes,
  rate_per_second: ratePerSecond,
  queue_config: JSON.stringify({ batches: totalBatches, size: batchSize, rate: ratePerSecond }),
  send_ready: totalRecipients > 0 ? "true" : "false"
};`,
          outputVariables: [
            { name: "total_batches", type: "number" },
            { name: "batch_size", type: "number" },
            { name: "estimated_duration_minutes", type: "number" },
            { name: "rate_per_second", type: "number" },
            { name: "queue_config", type: "string" },
            { name: "send_ready", type: "string" },
          ],
        },
      }),

      // 5. Send execution via SES API
      n("5", "hitApi", "Execute Bulk Send via AWS SES", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://email.us-east-1.amazonaws.com/v2/email/outbound-bulk-emails",
          headers: [
            {
              key: "Authorization",
              value: "AWS4-HMAC-SHA256 Credential={aws_access_key}",
            },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "FromEmailAddress", value: "{from_email}" },
            {
              key: "BulkEmailEntries",
              value:
                '[{"Destination":{"ToAddresses":["batch_recipients"]},"ReplacementEmailContent":{"ReplacementTemplate":{"ReplacementTemplateData":"{\\"subject\\":\\"{primary_subject}\\"}"}}}]',
            },
            {
              key: "DefaultContent",
              value:
                '{"Template":{"TemplateName":"{template_id}","TemplateData":"{\\"campaign_id\\":\\"{campaign_id}\\"}"}}',
            },
          ],
          variables: [
            {
              name: "BulkEmailEntryResults[0].Status",
              customName: "send_status",
              type: "string",
            },
            {
              name: "BulkEmailEntryResults[0].MessageId",
              customName: "ses_message_id",
              type: "string",
            },
          ],
        },
      }),

      // 6. Bounce/complaint handler condition
      n("6", "condition", "Check for Bounces or Complaints", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "send_status", operator: "eq", value: "Success" },
          ],
        },
      }),

      // 7. CRM writeback HubSpot
      hubspotPostNode(
        "7",
        "Write Campaign Results to HubSpot",
        1480,
        "crm/v3/objects/contacts/batch/update",
        [
          {
            key: "inputs",
            value:
              '[{"id":"{hs_contact_id}","properties":{"hs_email_last_send_date":"' +
              new Date().toISOString().split("T")[0] +
              '","ses_campaign_id":"{campaign_id}","ses_send_status":"{send_status}","ses_message_id":"{ses_message_id}"}}]',
          },
        ],
        [
          { name: "status", customName: "crm_update_status", type: "string" },
          {
            name: "results[0].id",
            customName: "updated_contact_id",
            type: "string",
          },
        ],
      ),

      // 8. Campaign analytics alerting Slack
      slackNode(
        "8",
        "Alert Campaign Analytics on Slack",
        1720,
        "BULK EMAIL CAMPAIGN SENT\\n*Campaign:* {campaign_id}\\n*Template:* {template_id}\\n*Total Recipients:* {valid_recipients} (suppressed: {suppressed_count})\\n*Segments:* {segment_count}\\n*Subject Lines:* {primary_subject} | {variant_a_subject} | {variant_b_subject}\\n*Send Rate:* {rate_per_second}/sec | Batches: {total_batches}\\n*Duration:* ~{estimated_duration_minutes} min\\n*Status:* {send_status}\\n*Est. Open Rate:* {estimated_open_rate}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1711. ACCOUNT BUILD FROM WEBSITE URL (8 nodes)
  // ===================================================================

  "Account Build from Website URL": {
    summary:
      "Fires when a website URL is submitted via webhook, scrapes and analyzes the site with runCode, enriches firmographics via Clearbit, generates a company profile with AI, identifies key contacts via API, enriches those contacts, creates the account and contacts in HubSpot, and delivers a rep briefing on Slack.",
    nodes: [
      trigger(
        "Webhook: Website URL submitted for account build",
        "webhook_app",
        {
          source: "sales_tools",
          event: "account.url_submitted",
          howItStarts:
            "Fires when a rep submits a prospect company's website URL — triggering automated account research, contact discovery, and CRM account creation from scratch",
          setup:
            "Configure sales tool webhook for URL submissions. Set OpenAI API key for AI profile generation. Provide Clearbit API key for firmographic and contact enrichment. Configure contact discovery API. Set HubSpot token for CRM creation. Configure Slack for rep briefings.",
          inputFields: [],
        },
      ),

      // 2. Website scraping analysis runCode
      n("2", "runCode", "Scrape and Analyze Website", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse the webhook payload and extract website URL and metadata
const payload = vars.webhook_payload || "";
let target_url = "";
let rep_email = "";
try {
  const parsed = typeof payload === "string" ? JSON.parse(payload) : payload;
  target_url = parsed.url || parsed.website || parsed.domain || parsed.target_url || "";
  rep_email = parsed.rep_email || parsed.requestor || parsed.submitted_by || "";
} catch(e) {
  // Try regex extraction if JSON parse fails
  const urlMatch = payload.match(/https?:\\/\\/[^\\s"',]+/);
  target_url = urlMatch ? urlMatch[0] : "";
  const emailMatch = payload.match(/[\\w.-]+@[\\w.-]+\\.[a-zA-Z]{2,}/);
  rep_email = emailMatch ? emailMatch[0] : "";
}
// Extract domain from URL
let domain = "";
try {
  domain = new URL(target_url).hostname.replace("www.", "");
} catch(e) {
  domain = target_url.replace(/https?:\\/\\//, "").replace("www.", "").split("/")[0];
}
return { target_url: target_url, domain: domain, rep_email: rep_email, scrape_status: domain ? "ready" : "invalid_url" };`,
          outputVariables: [
            { name: "target_url", type: "string" },
            { name: "domain", type: "string" },
            { name: "rep_email", type: "string" },
            { name: "scrape_status", type: "string" },
          ],
        },
      }),

      // 3. Firmographic enrichment Clearbit API
      n("3", "hitApi", "Enrich Firmographics via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "company_name", type: "string" },
            { name: "legalName", customName: "legal_name", type: "string" },
            {
              name: "description",
              customName: "company_description",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "category.subIndustry",
              customName: "sub_industry",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "string",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "estimated_revenue",
              type: "string",
            },
            { name: "geo.city", customName: "hq_city", type: "string" },
            { name: "geo.state", customName: "hq_state", type: "string" },
            { name: "geo.country", customName: "hq_country", type: "string" },
            { name: "tech", customName: "tech_stack", type: "string" },
            {
              name: "linkedin.handle",
              customName: "linkedin_handle",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI company profile generator
      aiNode(
        "4",
        "AI Generate Company Profile",
        760,
        "You are a B2B sales intelligence analyst. Given firmographic data from Clearbit, create a comprehensive company profile for the sales team. Include ideal customer profile (ICP) fit assessment, potential use cases, competitive landscape, estimated deal size, buying committee structure, and recommended approach strategy. Respond JSON: {icp_fit_score: number, icp_fit_reason: string, use_cases: string, competitive_landscape: string, estimated_deal_size: string, buying_committee: string, approach_strategy: string, key_talking_points: string, risk_factors: string}",
        "Company: {company_name}. Description: {company_description}. Industry: {industry} / {sub_industry}. Employees: {employee_count}. Revenue: {estimated_revenue}. Location: {hq_city}, {hq_state}, {hq_country}. Tech stack: {tech_stack}. LinkedIn: {linkedin_handle}. Build a complete sales profile.",
        [
          { field: "icp_fit_score", as: "icp_fit_score", type: "number" },
          { field: "icp_fit_reason", as: "icp_fit_reason", type: "string" },
          { field: "use_cases", as: "use_cases", type: "string" },
          {
            field: "competitive_landscape",
            as: "competitive_landscape",
            type: "string",
          },
          {
            field: "estimated_deal_size",
            as: "estimated_deal_size",
            type: "string",
          },
          { field: "buying_committee", as: "buying_committee", type: "string" },
          {
            field: "approach_strategy",
            as: "approach_strategy",
            type: "string",
          },
          {
            field: "key_talking_points",
            as: "key_talking_points",
            type: "string",
          },
          { field: "risk_factors", as: "risk_factors", type: "string" },
        ],
      ),

      // 5. Key contact identification API
      n("5", "hitApi", "Identify Key Contacts via Apollo", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "x-api-key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "q_organization_domains", value: "{domain}" },
            {
              key: "person_titles",
              value:
                '["VP Sales","Head of Marketing","CTO","VP Engineering","Director of Operations"]',
            },
            { key: "per_page", value: "5" },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "contact_1_name",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "contact_1_email",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "contact_1_title",
              type: "string",
            },
            {
              name: "people[1].name",
              customName: "contact_2_name",
              type: "string",
            },
            {
              name: "people[1].email",
              customName: "contact_2_email",
              type: "string",
            },
            {
              name: "people[1].title",
              customName: "contact_2_title",
              type: "string",
            },
            {
              name: "people[2].name",
              customName: "contact_3_name",
              type: "string",
            },
            {
              name: "people[2].email",
              customName: "contact_3_email",
              type: "string",
            },
            {
              name: "people[2].title",
              customName: "contact_3_title",
              type: "string",
            },
          ],
        },
      }),

      // 6. Contact enrichment API
      n("6", "hitApi", "Enrich Primary Contact via Clearbit", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={contact_1_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_contact_name",
              type: "string",
            },
            {
              name: "title",
              customName: "enriched_contact_title",
              type: "string",
            },
            {
              name: "linkedin.handle",
              customName: "contact_linkedin",
              type: "string",
            },
            { name: "bio", customName: "contact_bio", type: "string" },
          ],
        },
      }),

      // 7. CRM account + contact creation HubSpot
      hubspotPostNode(
        "7",
        "Create Account and Contacts in HubSpot",
        1480,
        "crm/v3/objects/companies",
        [
          {
            key: "properties",
            value:
              '{"name":"{company_name}","domain":"{domain}","industry":"{industry}","description":"{company_description}","numberofemployees":"{employee_count}","annualrevenue":"{estimated_revenue}","city":"{hq_city}","state":"{hq_state}","country":"{hq_country}","linkedin_company_page":"https://linkedin.com/company/{linkedin_handle}","icp_fit_score":"{icp_fit_score}","notes_last_contacted":"Account built from URL on ' +
              new Date().toISOString().split("T")[0] +
              '"}',
          },
        ],
        [
          { name: "id", customName: "hs_company_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "company_created_date",
            type: "string",
          },
        ],
      ),

      // 8. Rep briefing delivery Slack
      slackNode(
        "8",
        "Deliver Rep Briefing on Slack",
        1720,
        "NEW ACCOUNT BUILT FROM URL\\n*{company_name}* ({domain})\\nIndustry: {industry} / {sub_industry}\\nEmployees: {employee_count} | Revenue: {estimated_revenue}\\nHQ: {hq_city}, {hq_state}, {hq_country}\\n\\nICP Fit: {icp_fit_score}/100 — {icp_fit_reason}\\nEst. Deal Size: {estimated_deal_size}\\nApproach: {approach_strategy}\\n\\nKey Contacts:\\n1. {contact_1_name} — {contact_1_title} ({contact_1_email})\\n2. {contact_2_name} — {contact_2_title} ({contact_2_email})\\n3. {contact_3_name} — {contact_3_title} ({contact_3_email})\\n\\nTalking Points: {key_talking_points}\\nRisk Factors: {risk_factors}\\nHubSpot Company ID: {hs_company_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1712. ACCOUNT CONTACT GAP ANALYSIS (8 nodes)
  // ===================================================================

  "Account Contact Gap Analysis": {
    summary:
      "Runs weekly on schedule, inventories current contacts from HubSpot, builds a buying committee framework with runCode, classifies roles with AI, identifies gaps and priorities with runCode, discovers missing contacts via API, delivers a gap report via Slack and email, and creates CRM tasks in HubSpot.",
    nodes: [
      trigger("Schedule: Weekly contact gap analysis", "schedule", {
        schedule: "every Monday at 8:00 AM",
        howItStarts:
          "Runs every Monday morning to analyze all active deal accounts for buying committee coverage gaps — identifying missing decision-maker roles and prioritizing contact discovery efforts",
        setup:
          "Configure weekly schedule trigger. Set HubSpot token for contact inventory and task creation. Set OpenAI API key for role classification. Configure contact discovery API. Set Slack webhook and email for gap report delivery.",
        inputFields: [],
      }),

      // 2. Current contact inventory HubSpot
      hubspotGetNode(
        "2",
        "Inventory Current Contacts from HubSpot",
        280,
        "crm/v3/objects/deals?properties=dealname,amount,dealstage,pipeline&associations=contacts&limit=50",
        [
          {
            name: "results",
            customName: "deals_with_contacts",
            type: "string",
          },
          { name: "total", customName: "total_active_deals", type: "number" },
        ],
      ),

      // 3. Buying committee framework runCode
      n("3", "runCode", "Build Buying Committee Framework", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Define buying committee roles required for each deal stage
const requiredRoles = {
  "qualifiedtobuy": ["champion", "economic_buyer", "technical_evaluator"],
  "presentationscheduled": ["champion", "economic_buyer", "technical_evaluator", "end_user"],
  "decisionmakerboughtin": ["champion", "economic_buyer", "technical_evaluator", "end_user", "procurement"],
  "contractsent": ["champion", "economic_buyer", "technical_evaluator", "procurement", "legal"],
  "default": ["champion", "economic_buyer", "technical_evaluator"]
};
const roleDefinitions = {
  champion: "Internal advocate who drives the deal forward",
  economic_buyer: "Person with budget authority (VP/C-level)",
  technical_evaluator: "Person evaluating technical fit (IT/Engineering)",
  end_user: "Day-to-day user of the product",
  procurement: "Purchasing/procurement team member",
  legal: "Legal reviewer for contracts"
};
return {
  role_framework: JSON.stringify(requiredRoles),
  role_definitions: JSON.stringify(roleDefinitions),
  framework_version: "v2.1",
  total_roles_tracked: Object.keys(roleDefinitions).length.toString()
};`,
          outputVariables: [
            { name: "role_framework", type: "string" },
            { name: "role_definitions", type: "string" },
            { name: "framework_version", type: "string" },
            { name: "total_roles_tracked", type: "string" },
          ],
        },
      }),

      // 4. AI role classification
      aiNode(
        "4",
        "AI Classify Contact Roles",
        760,
        "You are a B2B sales org-chart analyst. Given the current contacts associated with active deals and the buying committee framework, classify each contact into a role (champion, economic_buyer, technical_evaluator, end_user, procurement, legal). Identify which deals have complete coverage and which have gaps. Respond JSON: {classified_contacts: string, deals_with_gaps: string, total_gaps: number, critical_gaps: string, gap_priority_ranking: string, coverage_percentage: number}",
        "Active deals and contacts: {deals_with_contacts}. Total deals: {total_active_deals}. Buying committee framework: {role_framework}. Role definitions: {role_definitions}. Classify all contacts and identify coverage gaps.",
        [
          {
            field: "classified_contacts",
            as: "classified_contacts",
            type: "string",
          },
          { field: "deals_with_gaps", as: "deals_with_gaps", type: "string" },
          { field: "total_gaps", as: "total_gaps", type: "number" },
          { field: "critical_gaps", as: "critical_gaps", type: "string" },
          {
            field: "gap_priority_ranking",
            as: "gap_priority_ranking",
            type: "string",
          },
          {
            field: "coverage_percentage",
            as: "coverage_percentage",
            type: "number",
          },
        ],
      ),

      // 5. Gap identification prioritization runCode
      n("5", "runCode", "Prioritize Gaps for Discovery", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prioritize contact gaps based on deal value and stage
const totalGaps = parseInt(vars.total_gaps) || 0;
const coveragePct = parseInt(vars.coverage_percentage) || 0;
const criticalGaps = vars.critical_gaps || "none";
let urgency = "low";
if (coveragePct < 50) urgency = "critical";
else if (coveragePct < 70) urgency = "high";
else if (coveragePct < 85) urgency = "medium";
const needsDiscovery = totalGaps > 0 ? "true" : "false";
const gapSummary = "Total gaps: " + totalGaps + " | Coverage: " + coveragePct + "% | Urgency: " + urgency;
return {
  gap_urgency: urgency,
  needs_discovery: needsDiscovery,
  gap_summary: gapSummary,
  discovery_priority: criticalGaps !== "none" ? "economic_buyer_first" : "champion_first",
  total_gaps_found: totalGaps
};`,
          outputVariables: [
            { name: "gap_urgency", type: "string" },
            { name: "needs_discovery", type: "string" },
            { name: "gap_summary", type: "string" },
            { name: "discovery_priority", type: "string" },
            { name: "total_gaps_found", type: "number" },
          ],
        },
      }),

      // 6. Contact discovery API
      n("6", "hitApi", "Discover Missing Contacts via Apollo", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "x-api-key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "person_seniorities", value: '["vp","director","c_suite"]' },
            { key: "per_page", value: "10" },
            { key: "page", value: "1" },
          ],
          variables: [
            {
              name: "people",
              customName: "discovered_contacts",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "total_discovered",
              type: "number",
            },
          ],
        },
      }),

      // 7. Gap report delivery Slack + email
      slackNode(
        "7",
        "Deliver Gap Report via Slack",
        1480,
        "WEEKLY CONTACT GAP ANALYSIS\\n*Coverage:* {coverage_percentage}% | *Urgency:* {gap_urgency}\\n*Total Active Deals:* {total_active_deals}\\n*Total Gaps Found:* {total_gaps_found}\\n*Critical Gaps:* {critical_gaps}\\n*Priority Ranking:* {gap_priority_ranking}\\n*Discovery Priority:* {discovery_priority}\\n*Contacts Discovered:* {total_discovered}\\n\\nAction: Review gap report and assign discovery tasks to SDRs.",
      ),

      // 8. CRM task creation HubSpot
      hubspotPostNode(
        "8",
        "Create Gap Discovery Tasks in HubSpot",
        1720,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_subject":"Contact Gap Discovery — {gap_urgency} priority","hs_task_body":"Coverage: {coverage_percentage}%. Gaps: {total_gaps_found}. Critical: {critical_gaps}. Discovery priority: {discovery_priority}. Review gap report and begin outreach to fill missing buying committee roles.","hs_task_status":"NOT_STARTED","hs_task_priority":"{gap_urgency}","hs_timestamp":"' +
              new Date().toISOString() +
              '"}',
          },
        ],
        [
          { name: "id", customName: "task_id", type: "string" },
          {
            name: "properties.hs_task_subject",
            customName: "task_subject",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1713. ADS INTELLIGENCE PERSONALIZATION (8 nodes)
  // ===================================================================

  "Ads Intelligence Personalization": {
    summary:
      "Fires when prospect outreach is requested via webhook, scrapes Meta Ads library, gathers Google Ads intelligence, analyzes ad strategy with AI, generates outreach angles with AI, scores relevance with runCode, pushes to the sequence via API, and logs intelligence to HubSpot.",
    nodes: [
      trigger("Webhook: Prospect outreach with ads intel", "webhook_app", {
        source: "sales_automation",
        event: "prospect.outreach_with_ads",
        howItStarts:
          "Fires when a rep requests ads-informed outreach for a prospect — triggering automated analysis of the prospect's ad spend on Meta and Google to generate personalized outreach angles based on their advertising strategy",
        setup:
          "Configure sales automation webhook for ads intel requests. Set Meta Ad Library API and Google Ads API credentials. Set OpenAI API key for strategy analysis and outreach generation. Configure sequence API and HubSpot token.",
        inputFields: [],
      }),

      // 2. Meta Ads library scrape API
      n("2", "hitApi", "Scrape Meta Ads Library", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v18.0/ads_archive?search_terms={prospect_company}&ad_reached_countries=['US']&fields=ad_creative_bodies,ad_creative_link_titles,ad_delivery_start_time,ad_delivery_stop_time,page_name&access_token={meta_access_token}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "data", customName: "meta_ads_data", type: "string" },
            {
              name: "data.length",
              customName: "meta_ad_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Google Ads intelligence API
      n("3", "hitApi", "Gather Google Ads Intelligence", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.name, campaign.status, ad_group.name, ad_group_ad.ad.final_urls, metrics.impressions, metrics.clicks, metrics.cost_micros FROM ad_group_ad WHERE campaign.status = 'ENABLED' LIMIT 20",
            },
          ],
          variables: [
            { name: "results", customName: "google_ads_data", type: "string" },
            {
              name: "results.length",
              customName: "google_ad_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI ad strategy analysis
      aiNode(
        "4",
        "AI Analyze Ad Strategy",
        760,
        "You are an advertising strategy analyst for B2B sales. Given a prospect's Meta and Google ad data, analyze their advertising strategy: messaging themes, target audiences, campaign objectives, spend patterns, and competitive positioning. Identify what they are promoting, what pain points they address in their ads, and any gaps in their strategy. Respond JSON: {messaging_themes: string, target_audience: string, campaign_objectives: string, spend_pattern: string, key_products_promoted: string, pain_points_addressed: string, strategy_gaps: string, competitive_angle: string}",
        "Company: {prospect_company}. Meta Ads ({meta_ad_count} active): {meta_ads_data}. Google Ads ({google_ad_count} active): {google_ads_data}. Analyze their complete advertising strategy.",
        [
          { field: "messaging_themes", as: "messaging_themes", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          {
            field: "campaign_objectives",
            as: "campaign_objectives",
            type: "string",
          },
          { field: "spend_pattern", as: "spend_pattern", type: "string" },
          {
            field: "key_products_promoted",
            as: "key_products_promoted",
            type: "string",
          },
          {
            field: "pain_points_addressed",
            as: "pain_points_addressed",
            type: "string",
          },
          { field: "strategy_gaps", as: "strategy_gaps", type: "string" },
          {
            field: "competitive_angle",
            as: "competitive_angle",
            type: "string",
          },
        ],
      ),

      // 5. AI outreach angle generator
      aiNode(
        "5",
        "AI Generate Ads-Informed Outreach",
        1000,
        "You are an elite sales copywriter who uses advertising intelligence for personalization. Given the prospect's ad strategy analysis, craft 2 outreach angles that reference their specific ads, messaging, or strategy gaps. Each angle must demonstrate you've studied their marketing. Include a subject line and opening paragraph for each. Respond JSON: {angle_1_subject: string, angle_1_opening: string, angle_2_subject: string, angle_2_opening: string, best_angle: string, intel_reference: string}",
        "Prospect: {prospect_company} ({prospect_email}). Ad themes: {messaging_themes}. Target audience: {target_audience}. Objectives: {campaign_objectives}. Products promoted: {key_products_promoted}. Strategy gaps: {strategy_gaps}. Competitive angle: {competitive_angle}. Generate ads-informed outreach angles.",
        [
          { field: "angle_1_subject", as: "angle_1_subject", type: "string" },
          { field: "angle_1_opening", as: "angle_1_opening", type: "string" },
          { field: "angle_2_subject", as: "angle_2_subject", type: "string" },
          { field: "angle_2_opening", as: "angle_2_opening", type: "string" },
          { field: "best_angle", as: "best_angle", type: "string" },
          { field: "intel_reference", as: "intel_reference", type: "string" },
        ],
      ),

      // 6. Relevance scoring runCode
      n("6", "runCode", "Score Outreach Relevance", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score the relevance of ads-informed outreach angles
const metaAdCount = parseInt(vars.meta_ad_count) || 0;
const googleAdCount = parseInt(vars.google_ad_count) || 0;
const totalAds = metaAdCount + googleAdCount;
let relevance_score = 50;
// More ad data = more personalization possible
if (totalAds > 10) relevance_score += 20;
else if (totalAds > 5) relevance_score += 15;
else if (totalAds > 0) relevance_score += 10;
// Check if strategy gaps were identified (better outreach hook)
const hasGaps = (vars.strategy_gaps || "").length > 20;
if (hasGaps) relevance_score += 15;
// Check competitive angle quality
const hasCompetitive = (vars.competitive_angle || "").length > 20;
if (hasCompetitive) relevance_score += 10;
const finalScore = Math.min(100, relevance_score);
const useAdsIntel = finalScore >= 60 ? "true" : "false";
return { relevance_score: finalScore, use_ads_intel: useAdsIntel, total_ads_analyzed: totalAds, confidence_level: finalScore >= 80 ? "high" : finalScore >= 60 ? "medium" : "low" };`,
          outputVariables: [
            { name: "relevance_score", type: "number" },
            { name: "use_ads_intel", type: "string" },
            { name: "total_ads_analyzed", type: "number" },
            { name: "confidence_level", type: "string" },
          ],
        },
      }),

      // 7. Sequence integration API
      n("7", "hitApi", "Push Ads-Informed Outreach to Sequence", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.outreach.io/api/v2/sequences/{sequence_id}/steps",
          headers: [
            { key: "Authorization", value: "Bearer {outreach_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"type":"sequenceStep","attributes":{"subject":"{angle_1_subject}","body":"{angle_1_opening}","adsIntelReference":"{intel_reference}","relevanceScore":"{relevance_score}","confidenceLevel":"{confidence_level}"}}',
            },
          ],
          variables: [
            { name: "data.id", customName: "outreach_step_id", type: "string" },
            {
              name: "data.attributes.status",
              customName: "step_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. CRM intelligence logging HubSpot
      hubspotPostNode(
        "8",
        "Log Ads Intelligence to HubSpot",
        1720,
        "crm/v3/objects/contacts/{hs_contact_id}/notes",
        [
          {
            key: "properties",
            value:
              '{"hs_note_body":"ADS INTELLIGENCE REPORT\\nMeta Ads: {meta_ad_count} active | Google Ads: {google_ad_count} active\\nMessaging: {messaging_themes}\\nAudience: {target_audience}\\nObjectives: {campaign_objectives}\\nGaps: {strategy_gaps}\\nBest Angle: {best_angle}\\nRelevance: {relevance_score}/100 ({confidence_level})","hs_timestamp":"' +
              new Date().toISOString() +
              '"}',
          },
        ],
        [{ name: "id", customName: "note_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1714. AFFILIATE PARTNER RECRUITMENT (8 nodes)
  // ===================================================================

  "Affiliate Partner Recruitment": {
    summary:
      "Manually triggered with a prospect list, researches prospects via Clearbit, scores audience fit with runCode, generates personalized pitches with AI, assigns commission tiers via condition, sends outreach email, waits 5 days, and sends a follow-up email.",
    nodes: [
      trigger("Manual: Affiliate prospect list loaded", "manual", {
        source: "partnerships",
        event: "affiliate.prospect_loaded",
        howItStarts:
          "Manually triggered by the partnerships team with a list of potential affiliate partners — including their name, email, website, and audience type — to run an automated recruitment sequence",
        setup:
          "Configure Clearbit API key for prospect research. Set OpenAI API key for personalized pitch generation. Configure email sending credentials for outreach sequences.",
        inputFields: [
          { name: "prospect_name", type: "string", example: "Sarah Miller" },
          {
            name: "prospect_email",
            type: "string",
            example: "sarah@techblog.io",
          },
          {
            name: "prospect_website",
            type: "string",
            example: "https://techblog.io",
          },
          {
            name: "prospect_audience_type",
            type: "string",
            example: "SaaS founders and marketers",
          },
          { name: "prospect_audience_size", type: "string", example: "45000" },
        ],
      }),

      // 2. Prospect research enrichment Clearbit API
      n("2", "hitApi", "Research Prospect via Clearbit", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={prospect_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            { name: "bio", customName: "enriched_bio", type: "string" },
            {
              name: "site.url",
              customName: "enriched_website",
              type: "string",
            },
            {
              name: "twitter.handle",
              customName: "twitter_handle",
              type: "string",
            },
            {
              name: "linkedin.handle",
              customName: "linkedin_handle",
              type: "string",
            },
            {
              name: "location",
              customName: "prospect_location",
              type: "string",
            },
          ],
        },
      }),

      // 3. Audience fit scoring runCode
      n("3", "runCode", "Score Audience Fit for Affiliate", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score the affiliate prospect's audience fit
const audienceType = (vars.prospect_audience_type || "").toLowerCase();
const audienceSize = parseInt(vars.prospect_audience_size) || 0;
const bio = (vars.enriched_bio || "").toLowerCase();
let fit_score = 40;
// Audience type relevance
const highFitKeywords = ["saas", "startup", "b2b", "tech", "software", "marketing", "sales"];
const mediumFitKeywords = ["business", "entrepreneur", "digital", "growth", "productivity"];
const matchedHigh = highFitKeywords.filter(k => audienceType.includes(k) || bio.includes(k));
const matchedMedium = mediumFitKeywords.filter(k => audienceType.includes(k) || bio.includes(k));
fit_score += matchedHigh.length * 10;
fit_score += matchedMedium.length * 5;
// Audience size scoring
if (audienceSize >= 100000) fit_score += 20;
else if (audienceSize >= 50000) fit_score += 15;
else if (audienceSize >= 10000) fit_score += 10;
else if (audienceSize >= 1000) fit_score += 5;
// Social presence bonus
if (vars.twitter_handle) fit_score += 5;
if (vars.linkedin_handle) fit_score += 5;
const finalScore = Math.min(100, fit_score);
const tier = finalScore >= 80 ? "premium" : finalScore >= 60 ? "standard" : "starter";
return { audience_fit_score: finalScore, commission_tier: tier, matched_keywords: matchedHigh.concat(matchedMedium).join(", ") || "none", has_social_presence: (vars.twitter_handle || vars.linkedin_handle) ? "true" : "false" };`,
          outputVariables: [
            { name: "audience_fit_score", type: "number" },
            { name: "commission_tier", type: "string" },
            { name: "matched_keywords", type: "string" },
            { name: "has_social_presence", type: "string" },
          ],
        },
      }),

      // 4. AI personalized pitch generation
      aiNode(
        "4",
        "AI Generate Personalized Affiliate Pitch",
        760,
        "You are an affiliate partnership recruiter. Given the prospect's background, audience fit, and commission tier, write a personalized recruitment pitch email. Reference their specific content, audience, and why their followers would benefit from the product. Be authentic, not salesy. Include specific commission rates based on tier. Respond JSON: {pitch_subject: string, pitch_body_html: string, commission_rate: string, key_selling_point: string, audience_benefit: string}",
        "Prospect: {prospect_name} ({enriched_title}). Bio: {enriched_bio}. Website: {prospect_website}. Audience: {prospect_audience_type} ({prospect_audience_size} followers). Fit score: {audience_fit_score}/100. Tier: {commission_tier}. Keywords matched: {matched_keywords}. Write a compelling affiliate recruitment pitch.",
        [
          { field: "pitch_subject", as: "pitch_subject", type: "string" },
          { field: "pitch_body_html", as: "pitch_body_html", type: "string" },
          { field: "commission_rate", as: "commission_rate", type: "string" },
          {
            field: "key_selling_point",
            as: "key_selling_point",
            type: "string",
          },
          { field: "audience_benefit", as: "audience_benefit", type: "string" },
        ],
      ),

      // 5. Commission tier assignment condition
      n("5", "condition", "Is Premium Tier Affiliate?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "commission_tier", operator: "eq", value: "premium" },
          ],
        },
      }),

      // 6. Outreach email send
      emailNode(
        "6",
        "Send Affiliate Recruitment Email",
        1240,
        "{prospect_email}",
        "{pitch_subject}",
        "{pitch_body_html}",
      ),

      // 7. Delay 5 days
      n("7", "delayAction", "Wait 5 Days for Response", 1480, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 8. Follow-up sequence email
      emailNode(
        "8",
        "Send Affiliate Follow-Up Email",
        1720,
        "{prospect_email}",
        "Quick follow-up — {prospect_name}, re: affiliate partnership",
        "<p>Hi {prospect_name},</p><p>I reached out last week about a potential affiliate partnership. I know you're busy, so I'll keep this brief.</p><p>We've seen affiliates with audiences similar to yours ({prospect_audience_type}) earn {commission_rate} commissions — and their followers genuinely appreciate the recommendation.</p><p>{audience_benefit}</p><p>Would a quick 15-minute call this week work to explore if this is a fit?</p><p>Best,<br/>The Partnerships Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1715. AFFILIATE RECRUITING PIPELINE (9 nodes)
  // ===================================================================

  "Affiliate Recruiting Pipeline": {
    summary:
      "Runs on schedule, scores customers for affiliate potential with runCode, researches influencers via API, personalizes recruitment pitches with AI, builds commission offers with runCode, sends multi-channel outreach email, tracks interest via condition, activates onboarding workflows via API, and logs pipeline analytics to Sheets.",
    nodes: [
      trigger("Schedule: Weekly affiliate pipeline review", "schedule", {
        schedule: "every Wednesday at 10:00 AM",
        howItStarts:
          "Runs weekly to evaluate existing customers and industry influencers for affiliate partnership potential — scoring advocacy likelihood, generating personalized pitches, and feeding the recruiting pipeline",
        setup:
          "Configure weekly schedule. Set OpenAI API key for pitch personalization. Configure influencer research API. Set email credentials for outreach. Configure onboarding API and Google Sheets for pipeline analytics.",
        inputFields: [],
      }),

      // 2. Customer affiliate scoring runCode
      n("2", "runCode", "Score Customers for Affiliate Potential", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score existing customers for affiliate recruiting potential
// In production, this would pull from CRM data
const criteria = {
  nps_threshold: 8,
  usage_months_min: 3,
  referral_history: true,
  social_presence: true
};
// Scoring logic for batch processing
let affiliate_score = 50;
const hasReferrals = vars.referral_count && parseInt(vars.referral_count) > 0;
if (hasReferrals) affiliate_score += 20;
const hasHighNps = vars.nps_score && parseInt(vars.nps_score) >= criteria.nps_threshold;
if (hasHighNps) affiliate_score += 15;
const isLongTerm = vars.months_active && parseInt(vars.months_active) >= criteria.usage_months_min;
if (isLongTerm) affiliate_score += 10;
const hasSocial = vars.social_followers && parseInt(vars.social_followers) > 1000;
if (hasSocial) affiliate_score += 15;
const isCandidate = affiliate_score >= 65 ? "true" : "false";
return { affiliate_score: Math.min(100, affiliate_score), is_candidate: isCandidate, scoring_criteria: JSON.stringify(criteria), candidate_type: affiliate_score >= 80 ? "high_value" : "standard" };`,
          outputVariables: [
            { name: "affiliate_score", type: "number" },
            { name: "is_candidate", type: "string" },
            { name: "scoring_criteria", type: "string" },
            { name: "candidate_type", type: "string" },
          ],
        },
      }),

      // 3. Influencer research API
      n("3", "hitApi", "Research Industry Influencers", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "x-api-key", value: "{apollo_api_key}" },
          ],
          body: [
            {
              key: "person_titles",
              value:
                '["content creator","blogger","influencer","thought leader","newsletter author"]',
            },
            { key: "q_keywords", value: "SaaS B2B technology" },
            { key: "per_page", value: "10" },
          ],
          variables: [
            {
              name: "people",
              customName: "influencer_results",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "total_influencers",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI recruitment pitch personalization
      aiNode(
        "4",
        "AI Personalize Recruitment Pitch",
        760,
        "You are an affiliate program recruiter. Given candidate data and influencer research, create a personalized recruitment pitch. Highlight mutual benefits, provide concrete earning projections, and reference the candidate's specific expertise and audience. Respond JSON: {pitch_subject: string, pitch_body: string, earning_projection: string, unique_hook: string, partnership_type: string}",
        "Candidate type: {candidate_type}. Affiliate score: {affiliate_score}/100. Influencer data: {influencer_results}. Create a compelling affiliate recruitment pitch with specific earning projections.",
        [
          { field: "pitch_subject", as: "recruit_subject", type: "string" },
          { field: "pitch_body", as: "recruit_body", type: "string" },
          {
            field: "earning_projection",
            as: "earning_projection",
            type: "string",
          },
          { field: "unique_hook", as: "unique_hook", type: "string" },
          { field: "partnership_type", as: "partnership_type", type: "string" },
        ],
      ),

      // 5. Commission offer builder runCode
      n("5", "runCode", "Build Commission Offer", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build commission offer based on candidate type and score
const candidateType = vars.candidate_type || "standard";
const score = parseInt(vars.affiliate_score) || 50;
let commission_pct = "20%";
let bonus_structure = "none";
let cookie_days = "30";
let tier_name = "Silver";
if (candidateType === "high_value" && score >= 85) {
  commission_pct = "35%";
  bonus_structure = "$500 first-month bonus + 5% recurring uplift at 10 referrals";
  cookie_days = "90";
  tier_name = "Platinum";
} else if (candidateType === "high_value") {
  commission_pct = "30%";
  bonus_structure = "$250 first-month bonus";
  cookie_days = "60";
  tier_name = "Gold";
} else if (score >= 65) {
  commission_pct = "25%";
  bonus_structure = "$100 bonus at 5 referrals";
  cookie_days = "45";
  tier_name = "Silver";
} else {
  commission_pct = "20%";
  bonus_structure = "none";
  cookie_days = "30";
  tier_name = "Bronze";
}
return { commission_pct: commission_pct, bonus_structure: bonus_structure, cookie_days: cookie_days, tier_name: tier_name, offer_summary: tier_name + ": " + commission_pct + " commission, " + cookie_days + "-day cookie" };`,
          outputVariables: [
            { name: "commission_pct", type: "string" },
            { name: "bonus_structure", type: "string" },
            { name: "cookie_days", type: "string" },
            { name: "tier_name", type: "string" },
            { name: "offer_summary", type: "string" },
          ],
        },
      }),

      // 6. Multi-channel recruitment outreach email
      emailNode(
        "6",
        "Send Affiliate Recruitment Outreach",
        1240,
        "{candidate_email}",
        "{recruit_subject}",
        "<p>{recruit_body}</p><hr/><p><strong>Your Offer:</strong> {offer_summary}</p><p>Commission: {commission_pct} on every referral | Cookie window: {cookie_days} days</p><p>Bonus: {bonus_structure}</p><p>Projected earnings: {earning_projection}</p><p><a href='https://partners.yourcompany.com/apply?tier={tier_name}'>Apply Now</a></p>",
      ),

      // 7. Interest tracking condition
      n("7", "condition", "Did Candidate Show Interest?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "is_candidate", operator: "eq", value: "true" },
          ],
        },
      }),

      // 8. Onboarding workflow activation API
      n("8", "hitApi", "Activate Affiliate Onboarding Workflow", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-platform.com/v1/affiliate/onboarding",
          headers: [
            { key: "Authorization", value: "Bearer {platform_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "email", value: "{candidate_email}" },
            { key: "tier", value: "{tier_name}" },
            { key: "commission_rate", value: "{commission_pct}" },
            { key: "cookie_window", value: "{cookie_days}" },
            { key: "bonus", value: "{bonus_structure}" },
          ],
          variables: [
            {
              name: "affiliate_id",
              customName: "affiliate_id",
              type: "string",
            },
            {
              name: "onboarding_status",
              customName: "onboarding_status",
              type: "string",
            },
          ],
        },
      }),

      // 9. Pipeline analytics Sheets
      sheetsNode(
        "9",
        "Log to Pipeline Analytics",
        1960,
        "AffiliatePipeline",
        '[["{candidate_email}","{candidate_type}","{affiliate_score}","{tier_name}","{commission_pct}","{cookie_days}","{earning_projection}","{partnership_type}","{onboarding_status}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1716. AGENCY REFERRAL PARTNER RECRUITING (8 nodes)
  // ===================================================================

  "Agency Referral Partner Recruiting": {
    summary:
      "Fires when an agency is identified via webhook, researches and scores the agency via Clearbit, analyzes service overlap with AI, generates a partnership pitch with AI, sends an outreach email sequence, waits 5 days, books a meeting via Calendly API, and tracks partnership performance in Sheets.",
    nodes: [
      trigger("Webhook: Agency partner identified", "webhook_app", {
        source: "partnerships",
        event: "agency.identified",
        howItStarts:
          "Fires when the partnerships team identifies a potential agency referral partner — including agency domain, contact email, and service categories — to automate a partnership recruitment sequence",
        setup:
          "Configure partnerships webhook for agency identification. Set Clearbit API key for agency research. Set OpenAI API key for overlap analysis and pitch generation. Configure email credentials. Set Calendly API for meeting booking. Configure Google Sheets for tracking.",
        inputFields: [],
      }),

      // 2. Agency research scoring Clearbit API
      n("2", "hitApi", "Research Agency via Clearbit", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={agency_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "agency_name", type: "string" },
            {
              name: "description",
              customName: "agency_description",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "agency_industry",
              type: "string",
            },
            {
              name: "category.subIndustry",
              customName: "agency_specialty",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "agency_size",
              type: "string",
            },
            { name: "geo.city", customName: "agency_city", type: "string" },
            {
              name: "geo.country",
              customName: "agency_country",
              type: "string",
            },
            { name: "tech", customName: "agency_tech_stack", type: "string" },
            {
              name: "linkedin.handle",
              customName: "agency_linkedin",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI service overlap analysis
      aiNode(
        "3",
        "AI Analyze Service Overlap",
        520,
        "You are a B2B partnerships strategist. Given an agency's profile, analyze the overlap between their services and our product. Identify which of their clients would benefit from our solution, the referral revenue potential, and how a partnership would strengthen their service offering. Respond JSON: {overlap_score: number, overlap_areas: string, client_benefit: string, referral_potential: string, partnership_value_prop: string, integration_points: string, competitive_risk: string}",
        "Agency: {agency_name}. Description: {agency_description}. Industry: {agency_industry} / {agency_specialty}. Size: {agency_size} employees. Location: {agency_city}, {agency_country}. Tech: {agency_tech_stack}. Analyze service overlap and partnership potential.",
        [
          { field: "overlap_score", as: "overlap_score", type: "number" },
          { field: "overlap_areas", as: "overlap_areas", type: "string" },
          { field: "client_benefit", as: "client_benefit", type: "string" },
          {
            field: "referral_potential",
            as: "referral_potential",
            type: "string",
          },
          {
            field: "partnership_value_prop",
            as: "partnership_value_prop",
            type: "string",
          },
          {
            field: "integration_points",
            as: "integration_points",
            type: "string",
          },
          { field: "competitive_risk", as: "competitive_risk", type: "string" },
        ],
      ),

      // 4. AI partnership pitch
      aiNode(
        "4",
        "AI Generate Partnership Pitch",
        760,
        "You are an agency partnerships recruiter. Write a personalized partnership pitch email that speaks to the agency's specific strengths, references their service areas, and clearly articulates mutual revenue opportunity. Be professional but warm — agency owners respond to specifics, not generic partnership spam. Respond JSON: {pitch_subject: string, pitch_body_html: string, revenue_model: string, next_step_suggestion: string}",
        "Agency: {agency_name} ({agency_specialty}). Size: {agency_size}. Overlap score: {overlap_score}/100. Overlap areas: {overlap_areas}. Client benefit: {client_benefit}. Referral potential: {referral_potential}. Value prop: {partnership_value_prop}. Write a compelling agency partnership pitch.",
        [
          {
            field: "pitch_subject",
            as: "agency_pitch_subject",
            type: "string",
          },
          { field: "pitch_body_html", as: "agency_pitch_body", type: "string" },
          { field: "revenue_model", as: "revenue_model", type: "string" },
          { field: "next_step_suggestion", as: "next_step", type: "string" },
        ],
      ),

      // 5. Outreach email sequence
      emailNode(
        "5",
        "Send Agency Partnership Email",
        1000,
        "{agency_contact_email}",
        "{agency_pitch_subject}",
        "{agency_pitch_body}",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 Days for Agency Response", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Meeting booking flow Calendly API
      n("7", "hitApi", "Create Calendly Scheduling Link", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.calendly.com/scheduling_links",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "max_event_count", value: "1" },
            {
              key: "owner",
              value:
                "https://api.calendly.com/event_types/{partnership_event_type_id}",
            },
            { key: "owner_type", value: "EventType" },
          ],
          variables: [
            {
              name: "resource.booking_url",
              customName: "calendly_booking_url",
              type: "string",
            },
            {
              name: "resource.owner",
              customName: "calendly_owner",
              type: "string",
            },
          ],
        },
      }),

      // 8. Partnership performance tracking Sheets
      sheetsNode(
        "8",
        "Track Partnership in Sheets",
        1720,
        "AgencyPartnerPipeline",
        '[["{agency_name}","{agency_domain}","{agency_contact_email}","{agency_specialty}","{agency_size}","{overlap_score}","{overlap_areas}","{referral_potential}","{revenue_model}","{calendly_booking_url}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1717. ALUMNI AFFINITY OUTREACH (7 nodes)
  // ===================================================================

  "Alumni Affinity Outreach": {
    summary:
      "Fires when a prospect list is submitted via webhook, fetches prospect education data via API, matches against internal alumni database with runCode, generates alumni-connection outreach with AI, routes to the right alumni rep via condition, sends the outreach email, and logs personalization to HubSpot.",
    nodes: [
      trigger("Webhook: Prospect list for alumni outreach", "webhook_app", {
        source: "sales_ops",
        event: "prospect.alumni_outreach_requested",
        howItStarts:
          "Fires when the sales ops team submits a prospect for alumni-affinity outreach — checking if any current reps share a university or school connection with the prospect, and using that shared connection for warmer outreach",
        setup:
          "Configure sales ops webhook for alumni outreach requests. Set education data API credentials. Set OpenAI API key for outreach generation. Configure email credentials. Set HubSpot token for personalization logging.",
        inputFields: [],
      }),

      // 2. Prospect education data fetch API
      n("2", "hitApi", "Fetch Prospect Education Data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={prospect_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "prospect_name",
              type: "string",
            },
            { name: "title", customName: "prospect_title", type: "string" },
            {
              name: "company.name",
              customName: "prospect_company",
              type: "string",
            },
            {
              name: "education",
              customName: "prospect_education",
              type: "string",
            },
            { name: "bio", customName: "prospect_bio", type: "string" },
            {
              name: "location",
              customName: "prospect_location",
              type: "string",
            },
          ],
        },
      }),

      // 3. Internal alumni database match runCode
      n("3", "runCode", "Match Against Alumni Database", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Match prospect education against internal rep alumni database
const prospectEdu = (vars.prospect_education || "").toLowerCase();
const prospectBio = (vars.prospect_bio || "").toLowerCase();
// Internal alumni database (in production, pulled from CRM/DB)
const alumniReps = [
  { name: "Alex Chen", email: "alex@company.com", schools: ["stanford", "mit"], grad_year: "2015" },
  { name: "Jamie Rodriguez", email: "jamie@company.com", schools: ["harvard", "wharton"], grad_year: "2016" },
  { name: "Morgan Lee", email: "morgan@company.com", schools: ["berkeley", "ucla"], grad_year: "2017" },
  { name: "Taylor Kim", email: "taylor@company.com", schools: ["columbia", "nyu"], grad_year: "2018" },
  { name: "Jordan Patel", email: "jordan@company.com", schools: ["michigan", "northwestern"], grad_year: "2016" }
];
let matched_rep = null;
let matched_school = "";
let match_strength = "none";
for (const rep of alumniReps) {
  for (const school of rep.schools) {
    if (prospectEdu.includes(school) || prospectBio.includes(school)) {
      matched_rep = rep;
      matched_school = school;
      match_strength = "strong";
      break;
    }
  }
  if (matched_rep) break;
}
const has_alumni_match = matched_rep ? "true" : "false";
return {
  has_alumni_match: has_alumni_match,
  matched_rep_name: matched_rep ? matched_rep.name : "none",
  matched_rep_email: matched_rep ? matched_rep.email : "none",
  matched_school: matched_school || "none",
  match_strength: match_strength,
  alumni_angle: matched_school ? "Shared " + matched_school + " connection" : "no_match"
};`,
          outputVariables: [
            { name: "has_alumni_match", type: "string" },
            { name: "matched_rep_name", type: "string" },
            { name: "matched_rep_email", type: "string" },
            { name: "matched_school", type: "string" },
            { name: "match_strength", type: "string" },
            { name: "alumni_angle", type: "string" },
          ],
        },
      }),

      // 4. AI alumni connection outreach
      aiNode(
        "4",
        "AI Generate Alumni Connection Outreach",
        760,
        "You are a sales email writer specializing in leveraging personal connections. Given that a prospect and a sales rep share an alumni connection, write a warm outreach email that naturally references the shared school. Do NOT make it the entire focus — weave it in as a genuine connection point while delivering relevant business value. If no alumni match exists, write a standard personalized cold email instead. Respond JSON: {email_subject: string, email_body_html: string, personalization_type: string, connection_reference: string}",
        "Prospect: {prospect_name}, Title: {prospect_title}, Company: {prospect_company}. Education: {prospect_education}. Location: {prospect_location}. Alumni match: {has_alumni_match}. School: {matched_school}. Rep: {matched_rep_name}. Match strength: {match_strength}. Write the outreach email.",
        [
          { field: "email_subject", as: "alumni_subject", type: "string" },
          { field: "email_body_html", as: "alumni_body", type: "string" },
          {
            field: "personalization_type",
            as: "personalization_type",
            type: "string",
          },
          {
            field: "connection_reference",
            as: "connection_reference",
            type: "string",
          },
        ],
      ),

      // 5. Alumni rep routing condition
      n("5", "condition", "Has Alumni Match for Routing?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "has_alumni_match", operator: "eq", value: "true" },
          ],
        },
      }),

      // 6. Outreach email send
      emailNode(
        "6",
        "Send Alumni Affinity Outreach Email",
        1240,
        "{prospect_email}",
        "{alumni_subject}",
        "{alumni_body}",
      ),

      // 7. CRM personalization logging HubSpot
      hubspotPostNode(
        "7",
        "Log Personalization to HubSpot",
        1480,
        "crm/v3/objects/contacts/{hs_contact_id}/notes",
        [
          {
            key: "properties",
            value:
              '{"hs_note_body":"ALUMNI AFFINITY OUTREACH\\nProspect: {prospect_name} ({prospect_title}) at {prospect_company}\\nAlumni Match: {has_alumni_match} — {matched_school}\\nMatched Rep: {matched_rep_name}\\nPersonalization: {personalization_type}\\nConnection: {connection_reference}\\nSubject: {alumni_subject}","hs_timestamp":"' +
              new Date().toISOString() +
              '"}',
          },
        ],
        [{ name: "id", customName: "note_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1718. BLOCKED DOMAIN SUPPRESSION (7 nodes)
  // ===================================================================

  "Blocked Domain Suppression": {
    summary:
      "Runs daily on schedule, retrieves the blocked domain list from Sheets, scans CRM contacts in HubSpot, identifies matches with runCode, executes suppression in HubSpot, guards new contact ingestion via condition, and sends an audit report via Sheets and Slack.",
    nodes: [
      trigger("Schedule: Daily blocked domain suppression", "schedule", {
        schedule: "every day at 6:00 AM",
        howItStarts:
          "Runs daily to scan all CRM contacts against a maintained blocked/competitor domain list — suppressing matches from outreach sequences and flagging any new contacts that slip through",
        setup:
          "Configure daily schedule trigger. Set Google Sheets credentials for blocked domain list. Set HubSpot token for CRM scanning and suppression. Configure Slack webhook for audit alerts.",
        inputFields: [],
      }),

      // 2. Blocked domain list retrieval Sheets
      n("2", "hitApi", "Retrieve Blocked Domain List from Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/BlockedDomains!A2:C500",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "values",
              customName: "blocked_domains_raw",
              type: "string",
            },
            {
              name: "values.length",
              customName: "total_blocked_domains",
              type: "number",
            },
          ],
        },
      }),

      // 3. CRM contact scan HubSpot
      hubspotGetNode(
        "3",
        "Scan CRM Contacts in HubSpot",
        520,
        "crm/v3/objects/contacts?properties=email,firstname,lastname,company,hs_email_optout,hs_lead_status&limit=100",
        [
          { name: "results", customName: "crm_contacts", type: "string" },
          {
            name: "total",
            customName: "total_contacts_scanned",
            type: "number",
          },
        ],
      ),

      // 4. Match identification classification runCode
      n("4", "runCode", "Identify Blocked Domain Matches", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse blocked domains and match against CRM contacts
let blockedDomains = [];
try {
  const raw = JSON.parse(vars.blocked_domains_raw || "[]");
  blockedDomains = raw.map(row => (row[0] || "").toLowerCase().trim()).filter(d => d.length > 0);
} catch(e) {
  blockedDomains = [];
}
let contacts = [];
try {
  contacts = JSON.parse(vars.crm_contacts || "[]");
} catch(e) {
  contacts = [];
}
let matched_contacts = [];
let suppression_count = 0;
for (const contact of contacts) {
  const email = (contact.properties?.email || "").toLowerCase();
  const domain = email.split("@")[1] || "";
  if (blockedDomains.includes(domain)) {
    matched_contacts.push({
      id: contact.id,
      email: email,
      domain: domain,
      name: (contact.properties?.firstname || "") + " " + (contact.properties?.lastname || "")
    });
    suppression_count++;
  }
}
const has_matches = suppression_count > 0 ? "true" : "false";
return {
  suppression_count: suppression_count,
  has_matches: has_matches,
  matched_contact_ids: matched_contacts.map(c => c.id).join(","),
  matched_emails: matched_contacts.map(c => c.email).join(", ") || "none",
  total_blocked_domains: blockedDomains.length,
  scan_coverage: contacts.length
};`,
          outputVariables: [
            { name: "suppression_count", type: "number" },
            { name: "has_matches", type: "string" },
            { name: "matched_contact_ids", type: "string" },
            { name: "matched_emails", type: "string" },
            { name: "total_blocked_domains", type: "number" },
            { name: "scan_coverage", type: "number" },
          ],
        },
      }),

      // 5. Suppression execution HubSpot
      hubspotPostNode(
        "5",
        "Suppress Blocked Contacts in HubSpot",
        1000,
        "crm/v3/objects/contacts/batch/update",
        [
          {
            key: "inputs",
            value:
              '[{"id":"{matched_contact_ids}","properties":{"hs_email_optout":"true","hs_lead_status":"BLOCKED_DOMAIN","notes_last_contacted":"Auto-suppressed: blocked domain on ' +
              new Date().toISOString().split("T")[0] +
              '"}}]',
          },
        ],
        [
          { name: "status", customName: "suppression_status", type: "string" },
          {
            name: "results.length",
            customName: "contacts_suppressed",
            type: "number",
          },
        ],
      ),

      // 6. New contact ingestion guard condition
      n("6", "condition", "Were Contacts Suppressed?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "has_matches", operator: "eq", value: "true" },
          ],
        },
      }),

      // 7. Audit report alerting Sheets + Slack
      slackNode(
        "7",
        "Send Suppression Audit Alert",
        1480,
        "DAILY BLOCKED DOMAIN SUPPRESSION REPORT\\n*Date:* " +
          new Date().toISOString().split("T")[0] +
          "\\n*Domains in blocklist:* {total_blocked_domains}\\n*Contacts scanned:* {scan_coverage}\\n*Matches found:* {suppression_count}\\n*Suppressed emails:* {matched_emails}\\n*Status:* {suppression_status}\\n\\nAction: Review suppression log and update blocklist if needed.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1719. BLOG READER NURTURE TO SDR HANDOFF (9 nodes)
  // ===================================================================

  "Blog Reader Nurture to SDR Handoff": {
    summary:
      "Fires when a multi-article read is detected via webhook, maps topic interest with runCode, assesses interest with AI, enriches the lead via Clearbit, qualifies ICP intent via condition, sends nurture content email, alerts the SDR on Slack, creates the lead in HubSpot, and tracks handoff outcomes in Sheets.",
    nodes: [
      trigger("Webhook: Multi-article blog read detected", "webhook_app", {
        source: "content_analytics",
        event: "reader.multi_article_detected",
        howItStarts:
          "Fires when a known or identified reader consumes 3+ blog articles in a single session — indicating research intent that may warrant nurture content and SDR follow-up",
        setup:
          "Configure content analytics webhook for multi-article detection. Set OpenAI API key for interest assessment. Provide Clearbit API key for lead enrichment. Configure email for nurture content. Set Slack webhook for SDR alerts. Set HubSpot token and Google Sheets credentials.",
        inputFields: [],
      }),

      // 2. Topic interest mapping runCode
      n("2", "runCode", "Map Topic Interest from Reading", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook payload and map topic interests
const payload = vars.webhook_payload || "";
let reader_email = "";
let reader_name = "";
let articles = [];
let total_articles = 0;
let session_duration = 0;
try {
  const parsed = typeof payload === "string" ? JSON.parse(payload) : payload;
  reader_email = parsed.email || parsed.reader_email || parsed.user_email || "";
  reader_name = parsed.name || parsed.reader_name || "";
  articles = parsed.articles || parsed.pages || parsed.content || [];
  total_articles = Array.isArray(articles) ? articles.length : parseInt(parsed.article_count || "0");
  session_duration = parseInt(parsed.session_duration || parsed.time_on_site || "0");
} catch(e) {
  const emailMatch = payload.match(/[\\w.-]+@[\\w.-]+\\.[a-zA-Z]{2,}/);
  reader_email = emailMatch ? emailMatch[0] : "";
}
// Map topics from article data
const topics = Array.isArray(articles) ? articles.map(a => a.category || a.topic || "general") : [];
const uniqueTopics = [...new Set(topics)];
const topicSummary = uniqueTopics.join(", ") || "mixed_topics";
const isHighIntent = total_articles >= 5 || session_duration > 600;
return {
  reader_email: reader_email,
  reader_name: reader_name,
  total_articles: total_articles || 3,
  session_duration_seconds: session_duration,
  topics: topicSummary,
  unique_topic_count: uniqueTopics.length || 1,
  is_high_intent: isHighIntent ? "true" : "false",
  reading_depth: total_articles >= 5 ? "deep" : "moderate"
};`,
          outputVariables: [
            { name: "reader_email", type: "string" },
            { name: "reader_name", type: "string" },
            { name: "total_articles", type: "number" },
            { name: "session_duration_seconds", type: "number" },
            { name: "topics", type: "string" },
            { name: "unique_topic_count", type: "number" },
            { name: "is_high_intent", type: "string" },
            { name: "reading_depth", type: "string" },
          ],
        },
      }),

      // 3. AI interest assessment
      aiNode(
        "3",
        "AI Assess Reader Interest Level",
        520,
        "You are a demand generation analyst. Given a blog reader's behavior (articles read, topics, session duration), assess their buying intent level and recommend the best nurture content. Determine if this reader is in awareness, consideration, or decision stage. Recommend a specific content piece to send. Respond JSON: {intent_level: string, buyer_stage: string, recommended_content_title: string, recommended_content_url: string, nurture_priority: string, interest_summary: string, sdr_handoff_ready: string}",
        "Reader: {reader_name} ({reader_email}). Articles read: {total_articles}. Session: {session_duration_seconds}s. Topics: {topics}. Unique topics: {unique_topic_count}. Reading depth: {reading_depth}. Assess intent and recommend next content.",
        [
          { field: "intent_level", as: "intent_level", type: "string" },
          { field: "buyer_stage", as: "buyer_stage", type: "string" },
          {
            field: "recommended_content_title",
            as: "recommended_content_title",
            type: "string",
          },
          {
            field: "recommended_content_url",
            as: "recommended_content_url",
            type: "string",
          },
          { field: "nurture_priority", as: "nurture_priority", type: "string" },
          { field: "interest_summary", as: "interest_summary", type: "string" },
          {
            field: "sdr_handoff_ready",
            as: "sdr_handoff_ready",
            type: "string",
          },
        ],
      ),

      // 4. Lead enrichment Clearbit API
      n("4", "hitApi", "Enrich Reader via Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={reader_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_size",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "company_industry",
              type: "string",
            },
          ],
        },
      }),

      // 5. ICP intent qualification condition
      n("5", "condition", "Is SDR Handoff Ready?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "sdr_handoff_ready", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. Nurture content email
      emailNode(
        "6",
        "Send Nurture Content Email",
        1240,
        "{reader_email}",
        "Based on your reading — {recommended_content_title}",
        "<p>Hi {enriched_name},</p><p>We noticed you've been exploring our content on {topics} — great taste! Based on your interests, we thought you'd find this particularly valuable:</p><p><strong>{recommended_content_title}</strong></p><p><a href='{recommended_content_url}'>Read now</a></p><p>This piece goes deeper into the challenges that {company_industry} companies like {enriched_company} face. We think it'll be especially relevant given your role as {enriched_title}.</p><p>Happy reading!</p>",
      ),

      // 7. SDR handoff alert Slack
      slackNode(
        "7",
        "Alert SDR for Handoff",
        1480,
        "BLOG READER SDR HANDOFF\\n*{enriched_name}* — {enriched_title} at {enriched_company}\\nIndustry: {company_industry} | Size: {company_size}\\n*Reading Behavior:*\\nArticles: {total_articles} | Session: {session_duration_seconds}s | Depth: {reading_depth}\\nTopics: {topics}\\n*Intent:* {intent_level} | Stage: {buyer_stage} | Priority: {nurture_priority}\\n*Summary:* {interest_summary}\\nNurture sent: {recommended_content_title}\\nAction: Follow up within 24 hours.",
      ),

      // 8. CRM lead creation HubSpot
      hubspotPostNode(
        "8",
        "Create Lead in HubSpot",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{reader_email}","firstname":"{enriched_name}","company":"{enriched_company}","jobtitle":"{enriched_title}","industry":"{company_industry}","hs_lead_status":"OPEN","lead_source":"blog_reader","blog_topics_read":"{topics}","blog_articles_count":"{total_articles}","buyer_stage":"{buyer_stage}","intent_level":"{intent_level}"}',
          },
        ],
        [{ name: "id", customName: "hs_contact_id", type: "string" }],
      ),

      // 9. Handoff outcome tracking Sheets
      sheetsNode(
        "9",
        "Track Handoff Outcome in Sheets",
        1960,
        "BlogReaderHandoffs",
        '[["{reader_email}","{enriched_name}","{enriched_title}","{enriched_company}","{company_industry}","{total_articles}","{topics}","{intent_level}","{buyer_stage}","{sdr_handoff_ready}","{nurture_priority}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1720. BOARD ADVISOR INTRODUCTION (7 nodes)
  // ===================================================================

  "Board Advisor Introduction": {
    summary:
      "Fires when an executive target is identified via webhook, maps the board advisor network with runCode, evaluates connection strength with AI, drafts an introduction request with AI, sends the advisor outreach email, tracks the introduction in HubSpot, and coordinates the deal team on Slack.",
    nodes: [
      trigger("Webhook: Executive target for board intro", "webhook_app", {
        source: "executive_sales",
        event: "target.board_intro_requested",
        howItStarts:
          "Fires when a deal team identifies a high-value executive target who could be reached through a board advisor or investor connection — triggering an automated introduction request workflow",
        setup:
          "Configure executive sales webhook for board intro requests. Set OpenAI API key for connection evaluation and intro drafting. Configure email credentials for advisor outreach. Set HubSpot token for tracking. Configure Slack for deal team coordination.",
        inputFields: [],
      }),

      // 2. Board advisor network mapping runCode
      n("2", "runCode", "Map Board Advisor Network", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook and map board advisor connections to the executive target
const payload = vars.webhook_payload || "";
let targetName = "";
let targetEmail = "";
let targetCompany = "";
let targetTitle = "";
let dealId = "";
let dealOwner = "";
try {
  const parsed = typeof payload === "string" ? JSON.parse(payload) : payload;
  targetName = parsed.target_name || parsed.executive_name || "";
  targetEmail = parsed.target_email || parsed.executive_email || "";
  targetCompany = parsed.target_company || parsed.company || "";
  targetTitle = parsed.target_title || parsed.title || "";
  dealId = parsed.deal_id || "";
  dealOwner = parsed.deal_owner || parsed.requester || "";
} catch(e) {}
// Board advisor network (in production, from CRM/investor DB)
const advisors = [
  { name: "Patricia Wells", email: "patricia@advisors.com", connections: ["microsoft", "google", "salesforce"], type: "board_member" },
  { name: "David Chang", email: "david@ventures.com", connections: ["stripe", "datadog", "snowflake"], type: "investor" },
  { name: "Rachel Torres", email: "rachel@capitalgroup.com", connections: ["hubspot", "zendesk", "intercom"], type: "investor" },
];
const companyLower = targetCompany.toLowerCase();
let bestAdvisor = null;
let connectionType = "none";
for (const advisor of advisors) {
  if (advisor.connections.some(c => companyLower.includes(c))) {
    bestAdvisor = advisor;
    connectionType = advisor.type;
    break;
  }
}
return {
  target_name: targetName,
  target_email: targetEmail,
  target_company: targetCompany,
  target_title: targetTitle,
  deal_id: dealId,
  deal_owner: dealOwner,
  advisor_name: bestAdvisor ? bestAdvisor.name : "none",
  advisor_email: bestAdvisor ? bestAdvisor.email : "none",
  connection_type: connectionType,
  has_connection: bestAdvisor ? "true" : "false"
};`,
          outputVariables: [
            { name: "target_name", type: "string" },
            { name: "target_email", type: "string" },
            { name: "target_company", type: "string" },
            { name: "target_title", type: "string" },
            { name: "deal_id", type: "string" },
            { name: "deal_owner", type: "string" },
            { name: "advisor_name", type: "string" },
            { name: "advisor_email", type: "string" },
            { name: "connection_type", type: "string" },
            { name: "has_connection", type: "string" },
          ],
        },
      }),

      // 3. AI connection strength evaluation
      aiNode(
        "3",
        "AI Evaluate Connection Strength",
        520,
        "You are a relationship intelligence analyst. Given a board advisor's connection to a target executive, evaluate the strength and appropriateness of requesting an introduction. Consider the advisor's relationship type (board member vs investor), the target's seniority, and potential awkwardness. Rate the connection and recommend the best approach. Respond JSON: {connection_strength: string, approach_recommendation: string, risk_level: string, intro_angle: string, timing_suggestion: string, alternative_path: string}",
        "Target: {target_name} ({target_title}) at {target_company}. Advisor: {advisor_name} ({connection_type}). Has connection: {has_connection}. Deal owner: {deal_owner}. Evaluate the introduction path.",
        [
          {
            field: "connection_strength",
            as: "connection_strength",
            type: "string",
          },
          {
            field: "approach_recommendation",
            as: "approach_recommendation",
            type: "string",
          },
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "intro_angle", as: "intro_angle", type: "string" },
          {
            field: "timing_suggestion",
            as: "timing_suggestion",
            type: "string",
          },
          { field: "alternative_path", as: "alternative_path", type: "string" },
        ],
      ),

      // 4. AI introduction request draft
      aiNode(
        "4",
        "AI Draft Introduction Request",
        760,
        "You are an executive communications specialist. Write a polished, concise introduction request email to a board advisor asking them to connect you with a target executive. Be respectful of the advisor's time, make the ask specific and easy to act on, and briefly explain why the introduction benefits all parties. Respond JSON: {intro_subject: string, intro_body_html: string, follow_up_note: string}",
        "Advisor: {advisor_name} ({connection_type}). Target: {target_name} ({target_title}) at {target_company}. Connection strength: {connection_strength}. Approach: {approach_recommendation}. Angle: {intro_angle}. Deal owner: {deal_owner}. Write the introduction request email.",
        [
          { field: "intro_subject", as: "intro_subject", type: "string" },
          { field: "intro_body_html", as: "intro_body", type: "string" },
          { field: "follow_up_note", as: "follow_up_note", type: "string" },
        ],
      ),

      // 5. Advisor outreach email
      emailNode(
        "5",
        "Send Introduction Request to Advisor",
        1000,
        "{advisor_email}",
        "{intro_subject}",
        "{intro_body}",
      ),

      // 6. Introduction tracking HubSpot
      hubspotPostNode(
        "6",
        "Track Introduction in HubSpot",
        1240,
        "crm/v3/objects/notes",
        [
          {
            key: "properties",
            value:
              '{"hs_note_body":"BOARD ADVISOR INTRODUCTION REQUEST\\nTarget: {target_name} ({target_title}) at {target_company}\\nAdvisor: {advisor_name} ({connection_type})\\nConnection Strength: {connection_strength}\\nRisk: {risk_level}\\nAngle: {intro_angle}\\nTiming: {timing_suggestion}\\nStatus: Requested","hs_timestamp":"' +
              new Date().toISOString() +
              '"}',
          },
          {
            key: "associations",
            value:
              '[{"to":{"id":"{deal_id}"},"types":[{"associationCategory":"HUBSPOT_DEFINED","associationTypeId":214}]}]',
          },
        ],
        [{ name: "id", customName: "note_id", type: "string" }],
      ),

      // 7. Deal team coordination Slack
      slackNode(
        "7",
        "Coordinate Deal Team on Slack",
        1480,
        "BOARD ADVISOR INTRODUCTION SENT\\n*Target:* {target_name} — {target_title} at {target_company}\\n*Advisor:* {advisor_name} ({connection_type})\\n*Connection:* {connection_strength} | Risk: {risk_level}\\n*Angle:* {intro_angle}\\n*Timing:* {timing_suggestion}\\n*Alternative:* {alternative_path}\\n*Follow-up:* {follow_up_note}\\n*Deal Owner:* {deal_owner}\\nAction: Monitor for advisor response and prepare for introduction.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1721. BOUNCE HANDLING RE-ENRICHMENT (7 nodes)
  // ===================================================================

  "Bounce Handling Re-Enrichment": {
    summary:
      "Fires when a bounce event arrives via webhook, classifies the bounce type with runCode, flags the contact in HubSpot, attempts re-enrichment via API, verifies the new email via API, updates the CRM record via condition, and notifies ops on Slack.",
    nodes: [
      trigger("Webhook: Email bounce event received", "webhook_app", {
        source: "email_provider",
        event: "email.bounced",
        howItStarts:
          "Fires when an email bounce notification is received from the sending provider — triggering automated bounce classification, contact flagging, and re-enrichment to recover the contact with a valid email address",
        setup:
          "Configure email provider webhook for bounce events. Set OpenAI API key if needed. Provide Clearbit API key for re-enrichment. Configure email verification API. Set HubSpot token for CRM updates. Configure Slack for ops notifications.",
        inputFields: [],
      }),

      // 2. Bounce classification runCode
      n("2", "runCode", "Classify Bounce Type", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse bounce event and classify
const payload = vars.webhook_payload || "";
let bounced_email = "";
let bounce_type = "unknown";
let bounce_code = "";
let bounce_message = "";
let contact_id = "";
let campaign_id = "";
try {
  const parsed = typeof payload === "string" ? JSON.parse(payload) : payload;
  bounced_email = parsed.email || parsed.recipient || parsed.to || "";
  bounce_type = parsed.bounce_type || parsed.type || parsed.category || "unknown";
  bounce_code = parsed.error_code || parsed.smtp_code || parsed.code || "";
  bounce_message = parsed.error_message || parsed.description || parsed.reason || "";
  contact_id = parsed.contact_id || parsed.hs_contact_id || "";
  campaign_id = parsed.campaign_id || parsed.message_id || "";
} catch(e) {
  const emailMatch = payload.match(/[\\w.-]+@[\\w.-]+\\.[a-zA-Z]{2,}/);
  bounced_email = emailMatch ? emailMatch[0] : "";
}
// Classify bounce
const code = parseInt(bounce_code) || 0;
let classification = "soft_bounce";
let is_permanent = "false";
let needs_reenrichment = "false";
if (bounce_type.includes("hard") || code === 550 || code === 551 || code === 552 || code === 553) {
  classification = "hard_bounce";
  is_permanent = "true";
  needs_reenrichment = "true";
} else if (bounce_type.includes("soft") || code === 450 || code === 452) {
  classification = "soft_bounce";
  is_permanent = "false";
  needs_reenrichment = "false";
} else if (bounce_type.includes("block") || code === 554) {
  classification = "blocked";
  is_permanent = "true";
  needs_reenrichment = "true";
}
const domain = bounced_email.split("@")[1] || "";
return { bounced_email: bounced_email, bounce_classification: classification, is_permanent: is_permanent, needs_reenrichment: needs_reenrichment, bounce_code: bounce_code, bounce_message: bounce_message, contact_id: contact_id, campaign_id: campaign_id, bounced_domain: domain };`,
          outputVariables: [
            { name: "bounced_email", type: "string" },
            { name: "bounce_classification", type: "string" },
            { name: "is_permanent", type: "string" },
            { name: "needs_reenrichment", type: "string" },
            { name: "bounce_code", type: "string" },
            { name: "bounce_message", type: "string" },
            { name: "contact_id", type: "string" },
            { name: "campaign_id", type: "string" },
            { name: "bounced_domain", type: "string" },
          ],
        },
      }),

      // 3. CRM contact flagging HubSpot
      hubspotPostNode(
        "3",
        "Flag Bounced Contact in HubSpot",
        520,
        "crm/v3/objects/contacts/{contact_id}",
        [
          {
            key: "properties",
            value:
              '{"hs_email_bounce":"true","hs_email_optout":"true","bounce_type":"{bounce_classification}","bounce_code":"{bounce_code}","bounce_date":"' +
              new Date().toISOString().split("T")[0] +
              '","notes_last_contacted":"Bounced: {bounce_classification} ({bounce_code}) — {bounce_message}"}',
          },
        ],
        [
          { name: "id", customName: "flagged_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "current_email",
            type: "string",
          },
        ],
      ),

      // 4. Re-enrichment attempt API
      n("4", "hitApi", "Re-Enrich Contact via Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={bounced_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            {
              name: "emailAddresses",
              customName: "alternate_emails",
              type: "string",
            },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
          ],
        },
      }),

      // 5. Email verification API
      n("5", "hitApi", "Verify Alternate Email", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_api_key}&email={alternate_emails}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "status",
              customName: "verification_status",
              type: "string",
            },
            {
              name: "sub_status",
              customName: "verification_sub_status",
              type: "string",
            },
            {
              name: "did_you_mean",
              customName: "suggested_email",
              type: "string",
            },
          ],
        },
      }),

      // 6. CRM record update condition
      n("6", "condition", "Is New Email Verified Valid?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "verification_status", operator: "eq", value: "valid" },
          ],
        },
      }),

      // 7. Ops notification Slack
      slackNode(
        "7",
        "Notify Ops of Bounce Result",
        1480,
        "BOUNCE HANDLING REPORT\\n*Bounced:* {bounced_email} ({bounce_classification})\\nCode: {bounce_code} — {bounce_message}\\nDomain: {bounced_domain}\\nPermanent: {is_permanent}\\n\\n*Re-enrichment:*\\nName: {enriched_name} | Company: {enriched_company}\\nAlternate emails: {alternate_emails}\\nVerification: {verification_status} ({verification_sub_status})\\nSuggested: {suggested_email}\\n\\nCRM contact {flagged_contact_id} has been flagged.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1722. BOUNCE MAINTENANCE CLEANUP (8 nodes)
  // ===================================================================

  "Bounce Maintenance Cleanup": {
    summary:
      "Runs daily on schedule, aggregates bounce data via API, classifies hard vs soft bounces with runCode, suppresses hard bounces in HubSpot, re-validates soft bounces via API, queues re-enrichment via condition, monitors sender reputation with runCode, and sends a cleanup report to Sheets and Slack.",
    nodes: [
      trigger("Schedule: Daily bounce maintenance cleanup", "schedule", {
        schedule: "every day at 5:00 AM",
        howItStarts:
          "Runs daily to process accumulated email bounces — separating hard bounces (permanent suppression) from soft bounces (re-validation), maintaining sender reputation, and keeping the CRM clean",
        setup:
          "Configure daily schedule. Set email provider API for bounce data aggregation. Set HubSpot token for contact suppression and updates. Configure email verification API for re-validation. Set Google Sheets and Slack credentials for reporting.",
        inputFields: [],
      }),

      // 2. Bounce data aggregation API
      n("2", "hitApi", "Aggregate Bounce Data from Email Provider", 280, {
        hitApiDetails: {
          method: "GET",
          url:
            "https://api.postmarkapp.com/bounces?count=100&offset=0&type=HardBounce&inactive=true&fromdate=" +
            new Date(Date.now() - 86400000).toISOString().split("T")[0],
          headers: [
            { key: "Accept", value: "application/json" },
            {
              key: "X-Postmark-Server-Token",
              value: "{postmark_server_token}",
            },
          ],
          body: [],
          variables: [
            { name: "TotalCount", customName: "total_bounces", type: "number" },
            { name: "Bounces", customName: "bounce_records", type: "string" },
          ],
        },
      }),

      // 3. Hard vs soft classification runCode
      n("3", "runCode", "Classify Hard vs Soft Bounces", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify bounces into hard and soft categories
let bounces = [];
try {
  bounces = JSON.parse(vars.bounce_records || "[]");
} catch(e) { bounces = []; }
const hardBounces = [];
const softBounces = [];
for (const b of bounces) {
  const type = (b.Type || b.type || "").toLowerCase();
  const email = b.Email || b.email || "";
  if (type.includes("hard") || type.includes("bad-mailbox") || type.includes("no-mailbox")) {
    hardBounces.push({ email: email, type: type, reason: b.Description || "" });
  } else {
    softBounces.push({ email: email, type: type, reason: b.Description || "" });
  }
}
return {
  hard_bounce_count: hardBounces.length,
  soft_bounce_count: softBounces.length,
  hard_bounce_emails: hardBounces.map(b => b.email).join(",") || "none",
  soft_bounce_emails: softBounces.map(b => b.email).join(",") || "none",
  total_processed: bounces.length,
  hard_bounce_rate: bounces.length > 0 ? Math.round((hardBounces.length / bounces.length) * 100) : 0
};`,
          outputVariables: [
            { name: "hard_bounce_count", type: "number" },
            { name: "soft_bounce_count", type: "number" },
            { name: "hard_bounce_emails", type: "string" },
            { name: "soft_bounce_emails", type: "string" },
            { name: "total_processed", type: "number" },
            { name: "hard_bounce_rate", type: "number" },
          ],
        },
      }),

      // 4. Hard bounce suppression HubSpot
      hubspotPostNode(
        "4",
        "Suppress Hard Bounces in HubSpot",
        760,
        "crm/v3/objects/contacts/batch/update",
        [
          {
            key: "inputs",
            value:
              '[{"properties":{"hs_email_optout":"true","hs_email_bounce":"true","hs_lead_status":"HARD_BOUNCED","bounce_suppressed_date":"' +
              new Date().toISOString().split("T")[0] +
              '"}}]',
          },
        ],
        [
          {
            name: "status",
            customName: "hard_suppression_status",
            type: "string",
          },
          {
            name: "results.length",
            customName: "hard_contacts_suppressed",
            type: "number",
          },
        ],
      ),

      // 5. Soft bounce re-validation API
      n("5", "hitApi", "Re-Validate Soft Bounce Emails", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.zerobounce.net/v2/validatebatch",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{zerobounce_api_key}" },
            { key: "email_batch", value: "{soft_bounce_emails}" },
          ],
          variables: [
            {
              name: "email_batch",
              customName: "validation_results",
              type: "string",
            },
            {
              name: "email_batch.length",
              customName: "validated_count",
              type: "number",
            },
          ],
        },
      }),

      // 6. Contact re-enrichment queue condition
      n("6", "condition", "Are There Recoverable Soft Bounces?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "soft_bounce_count", operator: "gt", value: "0" },
          ],
        },
      }),

      // 7. Sender reputation monitoring runCode
      n("7", "runCode", "Monitor Sender Reputation", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate sender reputation impact
const totalProcessed = parseInt(vars.total_processed) || 0;
const hardCount = parseInt(vars.hard_bounce_count) || 0;
const softCount = parseInt(vars.soft_bounce_count) || 0;
const hardRate = parseInt(vars.hard_bounce_rate) || 0;
let reputation_status = "healthy";
let action_required = "none";
if (hardRate > 5) {
  reputation_status = "at_risk";
  action_required = "Immediate list cleaning required — hard bounce rate exceeds 5%";
} else if (hardRate > 2) {
  reputation_status = "warning";
  action_required = "Schedule list audit — hard bounce rate approaching threshold";
} else {
  reputation_status = "healthy";
  action_required = "No action needed — bounce rates within acceptable range";
}
const daily_summary = "Processed: " + totalProcessed + " | Hard: " + hardCount + " (" + hardRate + "%) | Soft: " + softCount;
return { reputation_status: reputation_status, action_required: action_required, daily_summary: daily_summary, bounce_trend: hardRate > 3 ? "increasing" : "stable" };`,
          outputVariables: [
            { name: "reputation_status", type: "string" },
            { name: "action_required", type: "string" },
            { name: "daily_summary", type: "string" },
            { name: "bounce_trend", type: "string" },
          ],
        },
      }),

      // 8. Cleanup report Sheets + Slack
      slackNode(
        "8",
        "Send Cleanup Report via Slack",
        1720,
        "DAILY BOUNCE CLEANUP REPORT\\n*Date:* " +
          new Date().toISOString().split("T")[0] +
          "\\n*{daily_summary}*\\n\\nHard bounces suppressed: {hard_contacts_suppressed}\\nSoft bounces re-validated: {validated_count}\\n\\n*Sender Reputation:* {reputation_status} | Trend: {bounce_trend}\\n*Action:* {action_required}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1723. BROKEN LINK RECLAIM OUTREACH (8 nodes)
  // ===================================================================

  "Broken Link Reclaim Outreach": {
    summary:
      "Fires when a target site is discovered via webhook, detects broken links via API, analyzes content matches with AI, discovers site owner contact via API, generates an outreach email with AI, sends the outreach email, waits 7 days, and sends a follow-up email.",
    nodes: [
      trigger(
        "Webhook: Target site discovered for broken link reclaim",
        "webhook_app",
        {
          source: "seo_tools",
          event: "site.broken_link_opportunity",
          howItStarts:
            "Fires when the SEO tool identifies a website with broken outbound links that could be replaced with links to our content — a proven link-building strategy that provides value to the site owner",
          setup:
            "Configure SEO tool webhook for broken link opportunities. Set broken link checker API credentials. Set OpenAI API key for content matching and outreach generation. Configure contact discovery API. Set email credentials for outreach sequences.",
          inputFields: [],
        },
      ),

      // 2. Broken link detection API
      n("2", "hitApi", "Detect Broken Links on Target Site", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.ahrefs.com/v3/site-explorer/broken-backlinks",
          headers: [
            { key: "Authorization", value: "Bearer {ahrefs_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "target", value: "{target_site_url}" },
            { key: "mode", value: "domain" },
            { key: "limit", value: "20" },
          ],
          variables: [
            { name: "backlinks", customName: "broken_links", type: "string" },
            {
              name: "stats.total",
              customName: "total_broken_links",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI content match analysis
      aiNode(
        "3",
        "AI Analyze Content Match Opportunity",
        520,
        "You are an SEO link-building strategist. Given a list of broken links on a target website, identify which ones could be replaced with our content. For each match, explain the relevance, the value to the site owner, and draft the suggested replacement URL. Respond JSON: {best_match_url: string, match_relevance: string, replacement_content_title: string, replacement_content_url: string, value_to_site_owner: string, total_matchable: number, outreach_angle: string}",
        "Target site: {target_site_url}. Broken links found ({total_broken_links}): {broken_links}. Analyze which broken links we can replace with our content.",
        [
          { field: "best_match_url", as: "best_match_url", type: "string" },
          { field: "match_relevance", as: "match_relevance", type: "string" },
          {
            field: "replacement_content_title",
            as: "replacement_content_title",
            type: "string",
          },
          {
            field: "replacement_content_url",
            as: "replacement_content_url",
            type: "string",
          },
          {
            field: "value_to_site_owner",
            as: "value_to_site_owner",
            type: "string",
          },
          { field: "total_matchable", as: "total_matchable", type: "number" },
          { field: "outreach_angle", as: "outreach_angle", type: "string" },
        ],
      ),

      // 4. Site owner contact discovery API
      n("4", "hitApi", "Discover Site Owner Contact", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={target_site_domain}&api_key={hunter_api_key}&type=personal&limit=5",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "owner_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "owner_first_name",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "owner_last_name",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "owner_position",
              type: "string",
            },
            {
              name: "data.domain",
              customName: "verified_domain",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI outreach email generation
      aiNode(
        "5",
        "AI Generate Broken Link Outreach",
        1000,
        "You are a link-building outreach specialist. Write a helpful, non-spammy email to a website owner informing them about broken links on their site. Lead with value (you're helping them fix their site), mention the specific broken link, and suggest your content as a replacement only after establishing you're trying to help. Keep it under 120 words. Respond JSON: {outreach_subject: string, outreach_body_html: string}",
        "Site owner: {owner_first_name} {owner_last_name} ({owner_position}). Site: {target_site_url}. Broken link: {best_match_url}. Our replacement: {replacement_content_title} ({replacement_content_url}). Relevance: {match_relevance}. Angle: {outreach_angle}. Write a genuine, helpful outreach email.",
        [
          {
            field: "outreach_subject",
            as: "link_outreach_subject",
            type: "string",
          },
          {
            field: "outreach_body_html",
            as: "link_outreach_body",
            type: "string",
          },
        ],
      ),

      // 6. Outreach email send
      emailNode(
        "6",
        "Send Broken Link Outreach Email",
        1240,
        "{owner_email}",
        "{link_outreach_subject}",
        "{link_outreach_body}",
      ),

      // 7. Delay 7 days
      n("7", "delayAction", "Wait 7 Days for Response", 1480, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      // 8. Follow-up sequence email
      emailNode(
        "8",
        "Send Broken Link Follow-Up",
        1720,
        "{owner_email}",
        "Quick follow-up — broken link on {verified_domain}",
        "<p>Hi {owner_first_name},</p><p>I sent a note last week about a broken link I noticed on {target_site_url}.</p><p>The link pointing to <code>{best_match_url}</code> appears to be returning a 404. I thought you'd want to know since it affects the reader experience.</p><p>We have a similar resource — <strong>{replacement_content_title}</strong> — that covers the same topic: <a href='{replacement_content_url}'>{replacement_content_url}</a></p><p>Happy to help in any way! Just wanted to make sure you were aware.</p><p>Cheers,<br/>The Content Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1724. CRM API RETRY HANDLER (7 nodes)
  // ===================================================================

  "CRM API Retry Handler": {
    summary:
      "Fires when an API call is intercepted via webhook, classifies the error with runCode, manages the request queue with runCode, implements exponential backoff retry logic with runCode, manages rate limit budgets with runCode, alerts dead letter queue failures on Slack, and updates the health dashboard in Sheets.",
    nodes: [
      trigger("Webhook: CRM API call failure intercepted", "webhook_app", {
        source: "api_gateway",
        event: "api.call_failed",
        howItStarts:
          "Fires when a CRM API call fails — capturing the original request, error details, and retry context — to implement intelligent retry logic with exponential backoff and rate limit awareness",
        setup:
          "Configure API gateway webhook for failed call interception. No external API keys needed — this is an internal retry engine. Configure Slack webhook for dead letter queue alerts. Set Google Sheets credentials for health dashboard.",
        inputFields: [],
      }),

      // 2. Error classification engine runCode
      n("2", "runCode", "Classify API Error", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse and classify the API error
const payload = vars.webhook_payload || "";
let original_url = "";
let original_method = "";
let original_body = "";
let error_code = 0;
let error_message = "";
let request_id = "";
let attempt_number = 1;
try {
  const parsed = typeof payload === "string" ? JSON.parse(payload) : payload;
  original_url = parsed.url || parsed.endpoint || "";
  original_method = parsed.method || "POST";
  original_body = JSON.stringify(parsed.body || parsed.payload || {});
  error_code = parseInt(parsed.status_code || parsed.error_code || "0");
  error_message = parsed.error_message || parsed.message || parsed.error || "";
  request_id = parsed.request_id || parsed.id || "req_" + Date.now();
  attempt_number = parseInt(parsed.attempt || "1");
} catch(e) {}
let error_category = "unknown";
let is_retryable = "false";
let max_retries = 3;
if (error_code === 429) {
  error_category = "rate_limited";
  is_retryable = "true";
  max_retries = 5;
} else if (error_code >= 500 && error_code < 600) {
  error_category = "server_error";
  is_retryable = "true";
  max_retries = 3;
} else if (error_code === 408 || error_code === 504) {
  error_category = "timeout";
  is_retryable = "true";
  max_retries = 3;
} else if (error_code >= 400 && error_code < 500) {
  error_category = "client_error";
  is_retryable = "false";
  max_retries = 0;
} else {
  error_category = "network_error";
  is_retryable = "true";
  max_retries = 3;
}
const should_retry = is_retryable === "true" && attempt_number <= max_retries;
return { original_url: original_url, original_method: original_method, original_body: original_body, error_code: error_code, error_message: error_message, request_id: request_id, attempt_number: attempt_number, error_category: error_category, is_retryable: is_retryable, max_retries: max_retries, should_retry: should_retry ? "true" : "false" };`,
          outputVariables: [
            { name: "original_url", type: "string" },
            { name: "original_method", type: "string" },
            { name: "original_body", type: "string" },
            { name: "error_code", type: "number" },
            { name: "error_message", type: "string" },
            { name: "request_id", type: "string" },
            { name: "attempt_number", type: "number" },
            { name: "error_category", type: "string" },
            { name: "is_retryable", type: "string" },
            { name: "max_retries", type: "number" },
            { name: "should_retry", type: "string" },
          ],
        },
      }),

      // 3. Request queue manager runCode
      n("3", "runCode", "Manage Request Queue", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Manage the retry request queue
const requestId = vars.request_id || "unknown";
const shouldRetry = vars.should_retry === "true";
const attemptNum = parseInt(vars.attempt_number) || 1;
const maxRetries = parseInt(vars.max_retries) || 3;
const errorCategory = vars.error_category || "unknown";
let queue_action = "discard";
let queue_position = 0;
let priority = "normal";
if (shouldRetry) {
  queue_action = "enqueue";
  // Priority based on error type
  if (errorCategory === "rate_limited") {
    priority = "low"; // wait longer for rate limits
    queue_position = 100 + attemptNum;
  } else if (errorCategory === "timeout") {
    priority = "high"; // retry timeouts quickly
    queue_position = attemptNum;
  } else {
    priority = "normal";
    queue_position = 50 + attemptNum;
  }
} else {
  queue_action = attemptNum > maxRetries ? "dead_letter" : "discard";
  priority = "none";
}
return { queue_action: queue_action, queue_position: queue_position, queue_priority: priority, is_dead_letter: queue_action === "dead_letter" ? "true" : "false", next_attempt: shouldRetry ? (attemptNum + 1).toString() : "none" };`,
          outputVariables: [
            { name: "queue_action", type: "string" },
            { name: "queue_position", type: "number" },
            { name: "queue_priority", type: "string" },
            { name: "is_dead_letter", type: "string" },
            { name: "next_attempt", type: "string" },
          ],
        },
      }),

      // 4. Exponential backoff retry logic runCode
      n("4", "runCode", "Calculate Exponential Backoff", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate exponential backoff delay for retry
const attempt = parseInt(vars.attempt_number) || 1;
const errorCategory = vars.error_category || "unknown";
const shouldRetry = vars.should_retry === "true";
let baseDelayMs = 1000; // 1 second base
if (errorCategory === "rate_limited") baseDelayMs = 5000; // 5 seconds for rate limits
else if (errorCategory === "timeout") baseDelayMs = 2000; // 2 seconds for timeouts
// Exponential backoff with jitter
const exponentialDelay = baseDelayMs * Math.pow(2, attempt - 1);
const jitter = Math.floor(Math.random() * 1000);
const totalDelayMs = Math.min(exponentialDelay + jitter, 300000); // Max 5 minutes
const delaySeconds = Math.ceil(totalDelayMs / 1000);
return {
  backoff_delay_ms: totalDelayMs,
  backoff_delay_seconds: delaySeconds,
  backoff_formula: "base(" + baseDelayMs + "ms) * 2^(" + (attempt-1) + ") + jitter(" + jitter + "ms)",
  retry_at: new Date(Date.now() + totalDelayMs).toISOString(),
  retry_ready: shouldRetry ? "true" : "false"
};`,
          outputVariables: [
            { name: "backoff_delay_ms", type: "number" },
            { name: "backoff_delay_seconds", type: "number" },
            { name: "backoff_formula", type: "string" },
            { name: "retry_at", type: "string" },
            { name: "retry_ready", type: "string" },
          ],
        },
      }),

      // 5. Rate limit budget manager runCode
      n("5", "runCode", "Manage Rate Limit Budget", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Track and manage API rate limit budget
const errorCategory = vars.error_category || "unknown";
const url = vars.original_url || "";
const isRateLimited = errorCategory === "rate_limited";
// Rate limit budget tracking
const defaultBudget = { requests_per_second: 10, requests_per_minute: 100, daily_limit: 10000 };
let current_usage = {
  minute_count: isRateLimited ? 100 : 50, // assume high usage if rate limited
  daily_count: isRateLimited ? 8000 : 5000,
};
const minute_remaining = defaultBudget.requests_per_minute - current_usage.minute_count;
const daily_remaining = defaultBudget.daily_limit - current_usage.daily_count;
const budget_healthy = minute_remaining > 10 && daily_remaining > 500;
let throttle_recommendation = "none";
if (minute_remaining <= 0) throttle_recommendation = "pause_60_seconds";
else if (minute_remaining < 10) throttle_recommendation = "reduce_rate_50_percent";
else if (daily_remaining < 500) throttle_recommendation = "reduce_rate_25_percent";
return {
  minute_remaining: minute_remaining,
  daily_remaining: daily_remaining,
  budget_healthy: budget_healthy ? "true" : "false",
  throttle_recommendation: throttle_recommendation,
  rate_limit_status: isRateLimited ? "active_rate_limit" : "normal"
};`,
          outputVariables: [
            { name: "minute_remaining", type: "number" },
            { name: "daily_remaining", type: "number" },
            { name: "budget_healthy", type: "string" },
            { name: "throttle_recommendation", type: "string" },
            { name: "rate_limit_status", type: "string" },
          ],
        },
      }),

      // 6. Dead letter queue alerting Slack
      slackNode(
        "6",
        "Alert Dead Letter Queue on Slack",
        1240,
        "CRM API RETRY HANDLER\\n*Request:* {request_id}\\n*URL:* {original_method} {original_url}\\n*Error:* {error_code} — {error_category}\\n*Message:* {error_message}\\n*Attempt:* {attempt_number}/{max_retries}\\n*Queue:* {queue_action} (Priority: {queue_priority})\\n*Backoff:* {backoff_delay_seconds}s ({backoff_formula})\\n*Retry at:* {retry_at}\\n*Rate Budget:* {minute_remaining}/min | {daily_remaining}/day\\n*Throttle:* {throttle_recommendation}\\n*Dead Letter:* {is_dead_letter}",
      ),

      // 7. Health dashboard update Sheets
      sheetsNode(
        "7",
        "Update Health Dashboard in Sheets",
        1480,
        "ApiHealthDashboard",
        '[["{request_id}","{original_url}","{error_code}","{error_category}","{attempt_number}","{max_retries}","{queue_action}","{backoff_delay_seconds}","{rate_limit_status}","{throttle_recommendation}","{is_dead_letter}","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1725. CRM OWNERSHIP REASSIGNMENT (8 nodes)
  // ===================================================================

  "CRM Ownership Reassignment": {
    summary:
      "Fires when a reassignment is triggered via webhook, inventories affected records in HubSpot, determines new owners with runCode, executes priority-based reassignment in HubSpot, notifies new owners on Slack and email, sends customer communication emails, creates an audit trail in Sheets, and generates transition tasks in HubSpot.",
    nodes: [
      trigger("Webhook: CRM ownership reassignment triggered", "webhook_app", {
        source: "sales_ops",
        event: "ownership.reassignment_triggered",
        howItStarts:
          "Fires when a rep leaves, territory changes, or a manual reassignment is triggered — automating the handoff of all affected contacts, deals, and tasks to new owners with proper notifications and audit trails",
        setup:
          "Configure sales ops webhook for reassignment triggers. Set HubSpot token for record inventory, reassignment, and task creation. Configure Slack and email for notifications. Set Google Sheets for audit trails.",
        inputFields: [],
      }),

      // 2. Affected records inventory HubSpot
      hubspotGetNode(
        "2",
        "Inventory Affected Records in HubSpot",
        280,
        'crm/v3/objects/contacts?properties=email,firstname,lastname,company,hubspot_owner_id,hs_lead_status&filterGroups=[{"filters":[{"propertyName":"hubspot_owner_id","operator":"EQ","value":"{departing_owner_id}"}]}]&limit=100',
        [
          { name: "results", customName: "affected_contacts", type: "string" },
          {
            name: "total",
            customName: "total_affected_contacts",
            type: "number",
          },
        ],
      ),

      // 3. New owner determination runCode
      n("3", "runCode", "Determine New Owners for Records", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook for reassignment details and determine new owners
const payload = vars.webhook_payload || "";
let departingOwnerName = "";
let departingOwnerId = "";
let reason = "";
let effectiveDate = "";
let targetOwnerId = "";
let targetOwnerName = "";
let targetOwnerEmail = "";
try {
  const parsed = typeof payload === "string" ? JSON.parse(payload) : payload;
  departingOwnerName = parsed.departing_owner_name || parsed.from_owner || "";
  departingOwnerId = parsed.departing_owner_id || parsed.from_owner_id || "";
  reason = parsed.reason || parsed.reassignment_reason || "territory_change";
  effectiveDate = parsed.effective_date || new Date().toISOString().split("T")[0];
  targetOwnerId = parsed.target_owner_id || parsed.to_owner_id || "";
  targetOwnerName = parsed.target_owner_name || parsed.to_owner || "";
  targetOwnerEmail = parsed.target_owner_email || parsed.to_email || "";
} catch(e) {}
const totalAffected = parseInt(vars.total_affected_contacts) || 0;
// Priority assignment based on deal stage
let highPriorityCount = 0;
let normalPriorityCount = totalAffected;
return {
  departing_owner_name: departingOwnerName,
  departing_owner_id: departingOwnerId,
  reason: reason,
  effective_date: effectiveDate,
  new_owner_id: targetOwnerId,
  new_owner_name: targetOwnerName,
  new_owner_email: targetOwnerEmail,
  total_records_to_reassign: totalAffected,
  reassignment_ready: totalAffected > 0 ? "true" : "false"
};`,
          outputVariables: [
            { name: "departing_owner_name", type: "string" },
            { name: "departing_owner_id", type: "string" },
            { name: "reason", type: "string" },
            { name: "effective_date", type: "string" },
            { name: "new_owner_id", type: "string" },
            { name: "new_owner_name", type: "string" },
            { name: "new_owner_email", type: "string" },
            { name: "total_records_to_reassign", type: "number" },
            { name: "reassignment_ready", type: "string" },
          ],
        },
      }),

      // 4. Priority-based reassignment HubSpot
      hubspotPostNode(
        "4",
        "Reassign Records in HubSpot",
        760,
        "crm/v3/objects/contacts/batch/update",
        [
          {
            key: "inputs",
            value:
              '[{"properties":{"hubspot_owner_id":"{new_owner_id}","notes_last_contacted":"Ownership reassigned from {departing_owner_name} to {new_owner_name} on {effective_date}. Reason: {reason}"}}]',
          },
        ],
        [
          { name: "status", customName: "reassignment_status", type: "string" },
          {
            name: "results.length",
            customName: "records_reassigned",
            type: "number",
          },
        ],
      ),

      // 5. Notification to new owners Slack + email
      slackNode(
        "5",
        "Notify New Owners on Slack",
        1000,
        "CRM OWNERSHIP REASSIGNMENT\\n*From:* {departing_owner_name}\\n*To:* {new_owner_name} ({new_owner_email})\\n*Reason:* {reason}\\n*Effective:* {effective_date}\\n*Records Reassigned:* {records_reassigned} / {total_records_to_reassign}\\n*Status:* {reassignment_status}\\n\\nAction: Review your new accounts and schedule intro calls within 48 hours.",
      ),

      // 6. Customer communication email
      emailNode(
        "6",
        "Send Customer Transition Email",
        1240,
        "{customer_email}",
        "Your new point of contact at our company",
        "<p>Hi there,</p><p>We wanted to let you know that your account is now being managed by <strong>{new_owner_name}</strong>. {new_owner_name} is fully up to speed on your account and is excited to continue supporting you.</p><p>You can reach {new_owner_name} directly at <a href='mailto:{new_owner_email}'>{new_owner_email}</a>.</p><p>This transition is effective as of {effective_date}, and there will be no disruption to your service.</p><p>Welcome aboard, {new_owner_name}!</p><p>Best,<br/>The Team</p>",
      ),

      // 7. Audit trail creation Sheets
      sheetsNode(
        "7",
        "Create Reassignment Audit Trail",
        1480,
        "OwnershipAuditTrail",
        '[["{departing_owner_name}","{departing_owner_id}","{new_owner_name}","{new_owner_id}","{reason}","{effective_date}","{total_records_to_reassign}","{records_reassigned}","{reassignment_status}","' +
          new Date().toISOString() +
          '"]]',
      ),

      // 8. Transition task generation HubSpot
      hubspotPostNode(
        "8",
        "Generate Transition Tasks in HubSpot",
        1720,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_subject":"Account Transition Review — {total_records_to_reassign} records from {departing_owner_name}","hs_task_body":"Review all reassigned accounts. Schedule intro calls within 48 hours for active deals. Update account plans and next steps. Reason for transfer: {reason}.","hs_task_status":"NOT_STARTED","hs_task_priority":"HIGH","hubspot_owner_id":"{new_owner_id}","hs_timestamp":"' +
              new Date().toISOString() +
              '"}',
          },
        ],
        [
          { name: "id", customName: "transition_task_id", type: "string" },
          {
            name: "properties.hs_task_subject",
            customName: "task_subject",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1726. CRM-POWERED DISCOVERY CALL PREP (8 nodes)
  // ===================================================================

  "CRM-Powered Discovery Call Prep": {
    summary:
      "Fires when a meeting is scheduled via webhook, aggregates CRM data from HubSpot, analyzes email threads with runCode, pulls call recording insights via API, enriches company intelligence via Clearbit, generates a prep document with AI, delivers it on Slack, and creates a post-call follow-up task with a delay.",
    nodes: [
      trigger("Webhook: Discovery call meeting scheduled", "webhook_app", {
        source: "calendly",
        event: "invitee.created",
        howItStarts:
          "Fires when a discovery call is scheduled via Calendly — triggering automated aggregation of all available CRM data, email history, past call insights, and company intelligence into a comprehensive prep document for the rep",
        setup:
          "Configure Calendly webhook for meeting creation events. Set HubSpot token for CRM data aggregation. Set call recording API credentials. Provide Clearbit API key for company intelligence. Set OpenAI API key for prep document generation. Configure Slack for delivery.",
        inputFields: [],
      }),

      // 2. CRM data aggregation HubSpot
      hubspotGetNode(
        "2",
        "Aggregate CRM Data from HubSpot",
        280,
        "crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,company,jobtitle,industry,hs_lead_status,notes_last_contacted,lifecyclestage,num_notes,hs_analytics_source,hs_email_last_open_date,hs_email_last_click_date&associations=deals,notes",
        [
          {
            name: "properties.email",
            customName: "contact_email",
            type: "string",
          },
          {
            name: "properties.firstname",
            customName: "contact_first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "contact_last_name",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "contact_company",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "contact_title",
            type: "string",
          },
          {
            name: "properties.industry",
            customName: "contact_industry",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.hs_analytics_source",
            customName: "original_source",
            type: "string",
          },
          {
            name: "properties.hs_email_last_open_date",
            customName: "last_email_open",
            type: "string",
          },
          {
            name: "associations.deals.results",
            customName: "associated_deals",
            type: "string",
          },
        ],
      ),

      // 3. Email thread analysis runCode
      n("3", "runCode", "Analyze Email Thread History", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook for meeting details and analyze email context
const payload = vars.webhook_payload || "";
let meetingTime = "";
let meetingDuration = "";
let meetingType = "";
let inviteeEmail = "";
let inviteeName = "";
let contactId = "";
let repEmail = "";
try {
  const parsed = typeof payload === "string" ? JSON.parse(payload) : payload;
  meetingTime = parsed.event?.start_time || parsed.scheduled_at || "";
  meetingDuration = parsed.event?.duration || "30";
  meetingType = parsed.event?.name || "Discovery Call";
  inviteeEmail = parsed.invitee?.email || parsed.email || "";
  inviteeName = parsed.invitee?.name || parsed.name || "";
  contactId = parsed.contact_id || parsed.hs_contact_id || "";
  repEmail = parsed.event?.organizer_email || parsed.rep_email || "";
} catch(e) {}
// Analyze available data signals
const hasDeals = (vars.associated_deals || "").length > 5;
const hasEmailEngagement = (vars.last_email_open || "").length > 0;
const leadStatus = vars.lead_status || "unknown";
let prep_focus = "general_discovery";
if (hasDeals) prep_focus = "deal_progression";
else if (hasEmailEngagement) prep_focus = "engaged_prospect";
else prep_focus = "cold_discovery";
return {
  meeting_time: meetingTime,
  meeting_duration: meetingDuration,
  meeting_type: meetingType,
  invitee_email: inviteeEmail,
  invitee_name: inviteeName,
  contact_id: contactId,
  rep_email: repEmail,
  prep_focus: prep_focus,
  has_prior_deals: hasDeals ? "true" : "false",
  engagement_level: hasEmailEngagement ? "engaged" : "new"
};`,
          outputVariables: [
            { name: "meeting_time", type: "string" },
            { name: "meeting_duration", type: "string" },
            { name: "meeting_type", type: "string" },
            { name: "invitee_email", type: "string" },
            { name: "invitee_name", type: "string" },
            { name: "contact_id", type: "string" },
            { name: "rep_email", type: "string" },
            { name: "prep_focus", type: "string" },
            { name: "has_prior_deals", type: "string" },
            { name: "engagement_level", type: "string" },
          ],
        },
      }),

      // 4. Call recording insights API
      n("4", "hitApi", "Pull Call Recording Insights", 760, {
        hitApiDetails: {
          method: "GET",
          url:
            "https://api.gong.io/v2/calls?fromDateTime=2024-01-01T00:00:00Z&toDateTime=" +
            new Date().toISOString() +
            '&filter={"contactEmails":["{contact_email}"]}',
          headers: [
            { key: "Authorization", value: "Bearer {gong_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "records", customName: "prior_calls", type: "string" },
            {
              name: "records.totalRecords",
              customName: "total_prior_calls",
              type: "number",
            },
          ],
        },
      }),

      // 5. Company intelligence Clearbit API
      n("5", "hitApi", "Enrich Company Intelligence via Clearbit", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={contact_company_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "company_name", type: "string" },
            {
              name: "description",
              customName: "company_description",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "company_employees",
              type: "string",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            { name: "tech", customName: "company_tech_stack", type: "string" },
          ],
        },
      }),

      // 6. AI prep document generator
      aiNode(
        "6",
        "AI Generate Discovery Call Prep",
        1240,
        "You are an elite sales enablement strategist. Given all available data — CRM history, email engagement, prior call recordings, and company intelligence — create a comprehensive discovery call prep document. Include: key talking points, questions to ask, potential objections with rebuttals, relevant case studies to reference, and a recommended call flow. Respond JSON: {prep_summary: string, key_talking_points: string, discovery_questions: string, potential_objections: string, recommended_case_studies: string, call_flow: string, risk_factors: string, ideal_outcome: string}",
        "Contact: {contact_first_name} {contact_last_name} ({contact_title}) at {contact_company}. Industry: {company_industry}. Employees: {company_employees}. Revenue: {company_revenue}. Tech: {company_tech_stack}. Lead status: {lead_status}. Lifecycle: {lifecycle_stage}. Source: {original_source}. Prior calls: {total_prior_calls}. Engagement: {engagement_level}. Prep focus: {prep_focus}. Meeting: {meeting_type} on {meeting_time} ({meeting_duration} min). Generate the prep document.",
        [
          { field: "prep_summary", as: "prep_summary", type: "string" },
          {
            field: "key_talking_points",
            as: "key_talking_points",
            type: "string",
          },
          {
            field: "discovery_questions",
            as: "discovery_questions",
            type: "string",
          },
          {
            field: "potential_objections",
            as: "potential_objections",
            type: "string",
          },
          {
            field: "recommended_case_studies",
            as: "recommended_case_studies",
            type: "string",
          },
          { field: "call_flow", as: "call_flow", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          { field: "ideal_outcome", as: "ideal_outcome", type: "string" },
        ],
      ),

      // 7. Prep document delivery Slack
      slackNode(
        "7",
        "Deliver Prep Document on Slack",
        1480,
        "DISCOVERY CALL PREP\\n*{contact_first_name} {contact_last_name}* — {contact_title} at {contact_company}\\n*Meeting:* {meeting_type} | {meeting_time} ({meeting_duration} min)\\n*Company:* {company_employees} employees | {company_revenue} revenue | {company_industry}\\n*Engagement:* {engagement_level} | Prior calls: {total_prior_calls}\\n\\n*Summary:* {prep_summary}\\n\\n*Talking Points:* {key_talking_points}\\n\\n*Questions:* {discovery_questions}\\n\\n*Objections:* {potential_objections}\\n\\n*Case Studies:* {recommended_case_studies}\\n\\n*Call Flow:* {call_flow}\\n\\n*Risks:* {risk_factors}\\n*Ideal Outcome:* {ideal_outcome}",
      ),

      // 8. Post-call follow-up task delay
      n("8", "delayAction", "Schedule Post-Call Follow-Up Task", 1720, {
        delayDetails: { duration: 1, unit: "days" },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1727. CSV CLEANUP DELIVERABILITY CHECK (8 nodes)
  // ===================================================================

  "CSV Cleanup Deliverability Check": {
    summary:
      "Fires when a CSV is uploaded via webhook, parses and normalizes the data with runCode, deduplicates with runCode, validates email deliverability via API, scores data quality with runCode, splits clean vs rejected via condition, exports clean records to HubSpot, and generates a cleanup report in Sheets and Slack.",
    nodes: [
      trigger("Webhook: CSV file uploaded for cleanup", "webhook_app", {
        source: "data_tools",
        event: "csv.uploaded",
        howItStarts:
          "Fires when a CSV file is uploaded for data cleanup — triggering automated parsing, deduplication, email verification, and quality scoring before CRM import",
        setup:
          "Configure data tools webhook for CSV uploads. No external AI needed — uses runCode for processing. Set email verification API credentials. Set HubSpot token for CRM export. Configure Google Sheets and Slack for reporting.",
        inputFields: [],
      }),

      // 2. CSV parsing normalization runCode
      n("2", "runCode", "Parse and Normalize CSV Data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse CSV upload webhook and normalize data
const payload = vars.webhook_payload || "";
let records = [];
let totalRows = 0;
let fileName = "";
let uploadedBy = "";
try {
  const parsed = typeof payload === "string" ? JSON.parse(payload) : payload;
  records = parsed.records || parsed.data || parsed.rows || [];
  totalRows = Array.isArray(records) ? records.length : parseInt(parsed.total_rows || "0");
  fileName = parsed.file_name || parsed.filename || "unknown.csv";
  uploadedBy = parsed.uploaded_by || parsed.user || "";
} catch(e) {}
// Normalize common fields
let normalizedCount = 0;
let errorCount = 0;
for (const record of records) {
  try {
    if (record.email) record.email = record.email.trim().toLowerCase();
    if (record.name) {
      record.name = record.name.trim().replace(/\\s+/g, " ");
      const parts = record.name.split(" ");
      record.firstName = parts[0] || "";
      record.lastName = parts.slice(1).join(" ") || "";
    }
    if (record.phone) record.phone = record.phone.replace(/[^+\\d]/g, "");
    normalizedCount++;
  } catch(e) { errorCount++; }
}
return {
  total_rows: totalRows || records.length,
  normalized_count: normalizedCount,
  parse_errors: errorCount,
  file_name: fileName,
  uploaded_by: uploadedBy,
  records_json: JSON.stringify(records.slice(0, 100)), // first 100 for processing
  parse_status: errorCount === 0 ? "clean" : "has_errors"
};`,
          outputVariables: [
            { name: "total_rows", type: "number" },
            { name: "normalized_count", type: "number" },
            { name: "parse_errors", type: "number" },
            { name: "file_name", type: "string" },
            { name: "uploaded_by", type: "string" },
            { name: "records_json", type: "string" },
            { name: "parse_status", type: "string" },
          ],
        },
      }),

      // 3. Deduplication engine runCode
      n("3", "runCode", "Deduplicate Records", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Deduplicate records by email
let records = [];
try {
  records = JSON.parse(vars.records_json || "[]");
} catch(e) { records = []; }
const seen = new Set();
const unique = [];
const duplicates = [];
for (const record of records) {
  const key = (record.email || "").toLowerCase();
  if (!key) {
    duplicates.push({ ...record, reason: "missing_email" });
    continue;
  }
  if (seen.has(key)) {
    duplicates.push({ ...record, reason: "duplicate_email" });
    continue;
  }
  seen.add(key);
  unique.push(record);
}
return {
  unique_count: unique.length,
  duplicate_count: duplicates.length,
  unique_records_json: JSON.stringify(unique),
  duplicate_emails: duplicates.map(d => d.email || "empty").join(", ") || "none",
  dedup_rate: records.length > 0 ? Math.round((duplicates.length / records.length) * 100) : 0
};`,
          outputVariables: [
            { name: "unique_count", type: "number" },
            { name: "duplicate_count", type: "number" },
            { name: "unique_records_json", type: "string" },
            { name: "duplicate_emails", type: "string" },
            { name: "dedup_rate", type: "number" },
          ],
        },
      }),

      // 4. Email deliverability validation API
      n("4", "hitApi", "Validate Email Deliverability", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.zerobounce.net/v2/validatebatch",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{zerobounce_api_key}" },
            { key: "email_batch", value: "{unique_records_json}" },
          ],
          variables: [
            {
              name: "email_batch",
              customName: "validation_results",
              type: "string",
            },
            {
              name: "email_batch.length",
              customName: "total_validated",
              type: "number",
            },
          ],
        },
      }),

      // 5. Data quality scoring runCode
      n("5", "runCode", "Score Data Quality", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score overall data quality of the CSV
const totalRows = parseInt(vars.total_rows) || 0;
const uniqueCount = parseInt(vars.unique_count) || 0;
const duplicateCount = parseInt(vars.duplicate_count) || 0;
const parseErrors = parseInt(vars.parse_errors) || 0;
const dedupRate = parseInt(vars.dedup_rate) || 0;
let quality_score = 100;
// Penalize for duplicates
if (dedupRate > 20) quality_score -= 25;
else if (dedupRate > 10) quality_score -= 15;
else if (dedupRate > 5) quality_score -= 5;
// Penalize for parse errors
const errorRate = totalRows > 0 ? (parseErrors / totalRows) * 100 : 0;
if (errorRate > 10) quality_score -= 20;
else if (errorRate > 5) quality_score -= 10;
// Penalize for very small datasets
if (uniqueCount < 10) quality_score -= 10;
const finalScore = Math.max(0, quality_score);
const is_importable = finalScore >= 50 ? "true" : "false";
const quality_grade = finalScore >= 90 ? "A" : finalScore >= 75 ? "B" : finalScore >= 60 ? "C" : finalScore >= 50 ? "D" : "F";
return {
  quality_score: finalScore,
  quality_grade: quality_grade,
  is_importable: is_importable,
  clean_count: uniqueCount,
  rejected_count: duplicateCount + parseErrors,
  quality_summary: "Score: " + finalScore + "/100 (" + quality_grade + "). " + uniqueCount + " clean, " + (duplicateCount + parseErrors) + " rejected. Dedup rate: " + dedupRate + "%"
};`,
          outputVariables: [
            { name: "quality_score", type: "number" },
            { name: "quality_grade", type: "string" },
            { name: "is_importable", type: "string" },
            { name: "clean_count", type: "number" },
            { name: "rejected_count", type: "number" },
            { name: "quality_summary", type: "string" },
          ],
        },
      }),

      // 6. Clean vs rejected splitting condition
      n("6", "condition", "Is CSV Importable Quality?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "is_importable", operator: "eq", value: "true" },
          ],
        },
      }),

      // 7. CRM export HubSpot
      hubspotPostNode(
        "7",
        "Export Clean Records to HubSpot",
        1480,
        "crm/v3/imports",
        [
          {
            key: "importRequest",
            value:
              '{"name":"CSV Import — {file_name}","files":[{"fileName":"{file_name}","fileImportPage":{"hasHeader":true,"columnMappings":[{"columnObjectTypeId":"0-1","columnName":"email","propertyName":"email"},{"columnObjectTypeId":"0-1","columnName":"firstName","propertyName":"firstname"},{"columnObjectTypeId":"0-1","columnName":"lastName","propertyName":"lastname"},{"columnObjectTypeId":"0-1","columnName":"company","propertyName":"company"}]}}]}',
          },
        ],
        [
          { name: "id", customName: "import_id", type: "string" },
          { name: "state", customName: "import_state", type: "string" },
        ],
      ),

      // 8. Cleanup report Sheets + Slack
      slackNode(
        "8",
        "Send CSV Cleanup Report",
        1720,
        "CSV CLEANUP & DELIVERABILITY REPORT\\n*File:* {file_name} | Uploaded by: {uploaded_by}\\n*Total Rows:* {total_rows}\\n*Quality:* {quality_grade} ({quality_score}/100)\\n\\n*Processing Results:*\\nNormalized: {normalized_count} | Parse errors: {parse_errors}\\nUnique: {unique_count} | Duplicates: {duplicate_count} (dedup rate: {dedup_rate}%)\\nValidated: {total_validated}\\n\\n*Import:* {is_importable}\\nClean records: {clean_count} | Rejected: {rejected_count}\\nHubSpot import: {import_state} (ID: {import_id})\\n\\n{quality_summary}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },
};
